import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, CheckCircle, Users, FileText, ArrowRight, ArrowLeft, Calendar, Camera, Image, Type, AlignLeft, Palette, Briefcase, Award, GraduationCap, Link2, ThumbsUp, Download, Sparkles, X, TrendingUp, CreditCard, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import razorpayService from '../services/razorpayService';
import { track } from '../services/analyticsEvents';
import { proxyImage } from '../utils/imageProxy';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Dashboard = ({ results, profileUrl, questionnaireData, onBackToHero, isSample = false }) => {
  // Auth context for user info
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('feedback');
  const [copiedText, setCopiedText] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all'); // all | high | medium | low
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // Payment modal removed - going directly to Razorpay
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // Sample audits show the full dashboard with no paywall - they're public
  // marketing surfaces, not real user audits.
  const [isFullAccessUnlocked, setIsFullAccessUnlocked] = useState(isSample);
  const [paymentTriggerSource, setPaymentTriggerSource] = useState('download'); // 'download' | 'suggestions'
  const [isAutoDownloading, setIsAutoDownloading] = useState(false); // Track if auto-downloading from URL param
  const [showProfileInspirations, setShowProfileInspirations] = useState(false);
  const [showBannerInspirations, setShowBannerInspirations] = useState(false);
  const [failedBannerImageUrls, setFailedBannerImageUrls] = useState(() => new Set());
  
  // View all states for sections with many items
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);
  const [showAllExperience, setShowAllExperience] = useState(false);
  const [showAllCertifications, setShowAllCertifications] = useState(false);
  const [showAllVolunteering, setShowAllVolunteering] = useState(false);
  
  // Navigation bar state
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);

  const markBannerImageFailed = (url) => {
    if (!url) return;
    setFailedBannerImageUrls((previousUrls) => {
      if (previousUrls.has(url)) return previousUrls;
      const nextUrls = new Set(previousUrls);
      nextUrls.add(url);
      return nextUrls;
    });
  };
  
  /**
   * Normalize scraped data from Maestro API structure
   * Extracts fields from nested basic_info to root level
   */
  const normalizeScrapedData = (data) => {
    if (!data) return data;
    
    // If already normalized or no basic_info, return as-is
    if (!data.basic_info) return data;
    
    const basicInfo = data.basic_info;
    const location = basicInfo.location || {};
    
    // Create normalized structure
    return {
      ...data,
      // Profile images - critical mappings from documentation
      avatar: data.avatar || basicInfo.profile_picture_url || '',
      profile_picture_url: basicInfo.profile_picture_url || data.avatar || '',
      background_picture_url: basicInfo.background_picture_url || data.background_picture_url || '',
      banner_image: data.banner_image || basicInfo.background_picture_url || '',
      
      // Basic info
      name: data.name || basicInfo.fullname || '',
      fullname: basicInfo.fullname || data.name || '',
      headline: data.headline || basicInfo.headline || '',
      position: data.position || basicInfo.headline || '',
      public_identifier: data.public_identifier || basicInfo.public_identifier || '',
      about: data.about || basicInfo.about || '',
      
      // Location
      city: data.city || location.city || location.full || '',
      country_code: data.country_code || location.country_code || '',
      location: location.full || data.location || '',
      
      // Metrics
      followers: data.followers || basicInfo.follower_count || 0,
      connections: data.connections || basicInfo.connection_count || 0,
      
      // Flags
      is_creator: data.is_creator ?? basicInfo.is_creator ?? false,
      is_premium: data.is_premium ?? basicInfo.is_premium ?? false,
      
      // Normalize recommendations structure
      recommendations: data.recommendations_received || data.recommendations?.received_recommendations || data.recommendations || [],
      recommendations_received: data.recommendations_received || data.recommendations?.received_recommendations || [],
      
      // Normalize volunteering (handle hyphenated key from Maestro)
      volunteering: data.volunteering || data['volunteering-experiences'] || []
    };
  };
  
  
  // CRITICAL: Auto-trigger PDF download if opened with ?download=true
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldDownload = urlParams.get('download') === 'true';
    
    if (shouldDownload && results && !isGeneratingPDF && !isAutoDownloading) {
      
      // Show auto-download indicator
      setIsAutoDownloading(true);
      
      // DON'T auto-unlock - require payment for all users
      // User must pay even with ?download=true parameter
      
      // Scroll to top to show the notification
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Delay to ensure Dashboard is fully rendered, then trigger payment modal
      setTimeout(() => {
        handleDownloadClick(); // This will show the payment modal
      }, 2000); // 2 second delay to ensure everything is ready
    }
  }, [results, isAutoDownloading]); // Only trigger when results are loaded

  // Per-visit unlock - no persistence check needed
  // isFullAccessUnlocked starts as false and only becomes true after payment in this session
  useEffect(() => {
    if (isSample) {
      console.log('📋 [SAMPLE] Sample audit mode - paywall + analytics events suppressed');
    } else {
      console.log('🔒 [PAYMENT] Full access locked - payment required for this visit');
    }
  }, [isSample]);

  // Fire audit_completed once when Dashboard mounts with a real audit result.
  // Fire paywall_shown alongside it since the paywall UI is always present until
  // the user pays. Both are guarded so they only fire once per Dashboard mount.
  // Sample mode never fires conversion events - they're not real audits.
  useEffect(() => {
    if (!results || isSample) return;
    const score = typeof results?.aiOverallScore === 'number'
      ? results.aiOverallScore
      : (typeof results?.score === 'number' ? results.score : undefined);
    track.auditCompleted(score);
    if (!isFullAccessUnlocked) {
      const localePrice = razorpayService.getPriceForLocale
        ? razorpayService.getPriceForLocale()
        : { amount: 100, currency: 'USD' };
      track.paywallShown({
        amount: localePrice.amount,
        currency: localePrice.currency,
        source: 'dashboard_mount',
      });
    }
  }, [results, isSample]);


  const handleBack = () => {
    // Show inline confirmation instead of modal
    setShowExitModal(true);
  };

  const handleConfirmBack = () => {
    setShowExitModal(false);
    onBackToHero && onBackToHero();
  };

  const handleCancelBack = () => {
    setShowExitModal(false);
  };

  // Experience helper
  const getExperienceData = () => {
    if (results?.experience?.length) return results.experience;
    // Fallback to sessionStorage
    try {
      const urlKey = btoa(profileUrl || '').replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) return JSON.parse(raw).experience || [];
    } catch (_) {}
    return [];
  };

  // Format experience date range, e.g., 'Mar 2023 - Present'
  const formatExperienceDateRange = (exp) => {
    // First try duration field if available
    if (exp.duration && typeof exp.duration === 'string') {
      return exp.duration;
    }
    
    // Otherwise build from start_date/end_date objects
    const startMonth = exp.start_date?.month || '';
    const startYear = exp.start_date?.year || '';
    const endMonth = exp.end_date?.month || '';
    const endYear = exp.end_date?.year || '';
    
    const startStr = [startMonth, startYear].filter(Boolean).join(' ');
    const endStr = endYear ? [endMonth, endYear].filter(Boolean).join(' ') : 'Present';
    
    if (startStr) {
      return `${startStr} - ${endStr}`;
    }
    return '';
  };

  // Recommendations helper
  const getRecommendationsData = () => {
    // Primary source: results (AI-mapped object)
    if (Array.isArray(results?.recommendations) && results.recommendations.length) {
      return results.recommendations;
    }

    // Fallback to raw scraped data from sessionStorage (normalized)
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        if (Array.isArray(parsed?.recommendations) && parsed.recommendations.length) {
          return parsed.recommendations;
        }
        if (Array.isArray(parsed?.recommendations_received) && parsed.recommendations_received.length) {
          return parsed.recommendations_received;
        }
        if (Array.isArray(parsed?.recommendations?.received_recommendations) && parsed.recommendations.received_recommendations.length) {
          return parsed.recommendations.received_recommendations;
        }
      }
    } catch (_) {}

    return [];
  };

  // Honors & Awards helper
  const getHonorsAndAwardsData = () => {
    const candidates = [];
    try {
      candidates.push(
        results?.honorsAndAwards,
        results?.honors,
        results?.awards,
        results?.profile?.honorsAndAwards,
        results?.profile?.honors,
        results?.profile?.awards,
        results?.accomplishments?.honors,
        results?.accomplishments?.awards,
        results?.honors_and_awards
      );
      // also consider sections text inside results and parse into list
      const resSectionText = stripHtmlText(
        results?.sections?.honorsAndAwards?.current ||
        results?.sections?.honors?.current ||
        results?.honorsAndAwardsText ||
        ''
      );
      if (resSectionText) {
        const lines = resSectionText
          .split(/\n+|•|\u2022|\u25CF|\-|\*|,/g)
          .map(s => s.trim())
          .filter(Boolean)
          .filter(l => !/^found\s+\d+/i.test(l))
          .filter(l => !/honou?rs?\s*&?\s*awards/i.test(l))
          .slice(0, 100);
        if (lines.length) candidates.push(lines.map(t => ({ title: t })));
      }
    } catch (_) {}
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        candidates.push(
          parsed?.honorsAndAwards,
          parsed?.honors,
          parsed?.awards,
          parsed?.profile?.honorsAndAwards,
          parsed?.profile?.honors,
          parsed?.profile?.awards,
          parsed?.accomplishments?.honors,
          parsed?.accomplishments?.awards,
          parsed?.honors_and_awards
        );
        // Also check sections blob text and try to parse simple list
        const sectionText = stripHtmlText(parsed?.sections?.honorsAndAwards?.current || parsed?.sections?.honors?.current || '');
        if (typeof sectionText === 'string' && sectionText.trim()) {
          const lines = sectionText
            .split(/\n+|•|\u2022|\u25CF|\-|\*|,/g)
            .map(s => s.trim())
            .filter(Boolean)
            .filter(l => !/^found\s+\d+/i.test(l))
            .filter(l => !/honou?rs?\s*&?\s*awards/i.test(l));
          if (lines.length) candidates.push(lines.map(t => ({ title: t })));
        }
      }
    } catch (_) {}

    // First non-empty array
    const found = candidates.find(arr => Array.isArray(arr) && arr.length);
    if (!Array.isArray(found)) return [];
    // Deduplicate by title+issuer
    const seen = new Set();
    const out = [];
    for (const it of found) {
      const arr = Array.isArray(it) ? it : [];
      for (const a of arr) {
        const key = [a.title || a.name || '', a.issuer || a.organization || ''].join('|').toLowerCase();
        if (key.trim() && !seen.has(key)) { seen.add(key); out.push(a); }
      }
    }
    return out.length ? out : found;
  };

  // Format ISO or free-form date to 'Mon YYYY'
  const formatMonthYear = (value) => {
    const s = String(value || '').trim();
    if (!s) return '';
    // Accept ISO 'YYYY-MM-DD' or 'YYYY-MM' or 'YYYY'
    const d = new Date(s);
    if (!isNaN(d)) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[d.getMonth()]} ${d.getFullYear()}`;
    }
    // Fallback: extract parts
    const m = s.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i);
    const y = s.match(/\b(19|20)\d{2}\b/);
    if (m && y) return `${m[0].slice(0,3)} ${y[0]}`;
    if (y) return y[0];
    return s;
  };

  // Helpers for displaying optional lists cleanly
  const stripHtmlText = (html) => {
    if (typeof html !== 'string') return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  };

  const formatChips = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(String).filter(Boolean).map(s => s.trim()).filter(Boolean);
    if (typeof val === 'string') return val.split(/,|\n|•|\u2022|\u25CF/).map(s => s.trim()).filter(Boolean);
    return [];
  };

  const hasVal = (v) => v !== undefined && v !== null && String(v).trim() !== '';

  // Skills & Endorsements helper
  const getSkillsData = () => {
    if (results?.skills?.length) return results.skills;
    try {
      const urlKey = btoa(profileUrl || '').replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) return JSON.parse(raw).skills || [];
    } catch (_) {}
    return [];
  };

  // Languages helper
  const getLanguagesData = () => {
    return results?.languages || [];
  };

  // Volunteering helper
  const getVolunteeringData = () => {
    return results?.volunteering || [];
  };

  // Featured content helper
  const getFeaturedData = () => {
    return results?.featured || [];
  };

  // Format education date range like 'Oct 2017 – Oct 2019'
  const formatEducationDateRange = (edu) => {
    // First try duration field if available
    if (edu.duration && typeof edu.duration === 'string') {
      return edu.duration;
    }
    
    // Otherwise build from start_date/end_date objects
    const startMonth = edu.start_date?.month || '';
    const startYear = edu.start_date?.year || '';
    const endMonth = edu.end_date?.month || '';
    const endYear = edu.end_date?.year || '';
    
    const startStr = [startMonth, startYear].filter(Boolean).join(' ');
    const endStr = [endMonth, endYear].filter(Boolean).join(' ');
    
    if (startStr && endStr) {
      return `${startStr} - ${endStr}`;
    }
    if (startStr) {
      return `${startStr} - Present`;
    }
    return '';
  };

  // Education data retrieval helper
  const getEducationData = () => {
    try {
      if (Array.isArray(results?.education) && results.education.length) {
        return results.education;
      }
    } catch (_) {}
    
    try {
      // Fallback to sessionStorage
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.education) && parsed.education.length) {
          // Normalize field names from sessionStorage too
          return parsed.education.map(item => ({
            ...item,
            school: item.school || item.title || '',
            school_logo_url: item.school_logo_url || item.institute_logo_url || ''
          }));
        }
      }
    } catch (_) {}
    
    return [];
  };

  const getCertificationsData = () => {
    if (results?.certifications?.length) return results.certifications;
    try {
      const urlKey = btoa(profileUrl || '').replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) return JSON.parse(raw).certifications || [];
    } catch (_) {}
    return [];
  };

  // Resolve LinkedIn profile URL strictly (only the URL, no prefix/comments)
  const getProfileUrlText = (fallback) => {
    const candidates = [];
    try {
      candidates.push(
        results?.profile?.url,
        results?.url
      );
    } catch (_) {}
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        candidates.push(
          parsed?.profile?.url,
          parsed?.url,
          parsed?.input_url,
          parsed?.linkedin_url
        );
      }
    } catch (_) {}

    const urlRegex = /https?:\/\/[^\s"']+/i;
    const sanitize = (s) => {
      if (typeof s !== 'string') return '';
      const match = s.match(urlRegex);
      return match ? match[0].trim() : s.trim();
    };

    const found = candidates.find((x) => typeof x === 'string' && x.trim().length > 0);
    if (found) return sanitize(found);
    if (typeof fallback === 'string') return sanitize(fallback);
    return '';
  };

  // Resolve banner image URL
  const getBannerImageUrl = () => {
    const candidates = [];
    // from results if present
    try {
      candidates.push(
        results?.profile?.bannerUrl,
        results?.bannerUrl,
        results?.banner_image,
        results?.background_picture_url,
        results?.background_cover_image_url,
        results?.profile?.coverImage,
        results?.profile?.backgroundImage,
        results?.profile?.bannerImage,
        results?.profile?.backgroundImageUrl,
        results?.profile?.coverPhotoUrl
      );
    } catch (_) {}
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        candidates.push(
          parsed?.bannerUrl,
          parsed?.banner_image,
          parsed?.background_picture_url,
          parsed?.basic_info?.background_picture_url,
          parsed?.background_cover_image_url,
          parsed?.coverImage,
          parsed?.backgroundImage,
          parsed?.bannerImage,
          parsed?.profile?.bannerUrl,
          parsed?.backgroundImageUrl,
          parsed?.coverPhotoUrl
        );
      }
    } catch (_) {}
    const foundUrl = candidates.find((x) => typeof x === 'string' && x.trim().length > 0);
    return foundUrl;
  };

  // Resolve About text strictly (no prefixes, quotes, or trailing ellipsis)
  const getAboutText = (fallback) => {
    const candidates = [];
    try {
      candidates.push(
        results?.profile?.about,
        results?.about,
        results?.basic_info?.about
      );
    } catch (_) {}
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        candidates.push(
          parsed?.profile?.about,
          parsed?.about,
          parsed?.basic_info?.about
        );
      }
    } catch (_) {}
    const sanitize = (s) => {
      if (typeof s !== 'string') return '';
      let t = s.trim();
      // strip common prefixes like "About section found (xxxx characters):"
      t = t.replace(/^about\s*section.*?:\s*/i, '');
      // remove wrapping quotes
      const quotePairs = [["\"","\""],["'","'"],["“","”"],["‘","’"]];
      for (const [lq, rq] of quotePairs) {
        if (t.startsWith(lq) && t.endsWith(rq)) {
          t = t.slice(lq.length, t.length - rq.length).trim();
          break;
        }
      }
      // remove trailing ellipsis
      t = t.replace(/\.{3,}$/g, '').trim();
      return t;
    };

    const found = candidates.find((x) => typeof x === 'string' && x.trim().length > 0);
    if (found) return sanitize(found);
    if (typeof fallback === 'string') return sanitize(fallback);
    return '';
  };

  // Resolve headline text strictly (no prefixes)
  const getHeadlineText = (fallback) => {
    const candidates = [];
    try {
      candidates.push(
        results?.profile?.headline,
        results?.headline
      );
    } catch (_) {}
    try {
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        candidates.push(
          parsed?.profile?.headline,
          parsed?.headline
        );
      }
    } catch (_) {}
    const sanitize = (s) => {
      if (typeof s !== 'string') return '';
      let t = s.trim();
      // Remove wrapping quotes (straight or curly)
      const quotePairs = [
        ['"', '"'],
        ["'", "'"],
        ['“', '”'],
        ['‘', '’']
      ];
      for (const [lq, rq] of quotePairs) {
        if (t.startsWith(lq) && t.endsWith(rq)) {
          t = t.slice(lq.length, t.length - rq.length).trim();
          break;
        }
      }
      // Remove trailing ellipsis
      t = t.replace(/\.{3,}$/g, '').trim();
      return t;
    };

    const found = candidates.find((x) => typeof x === 'string' && x.trim().length > 0);
    if (found) return sanitize(found);
    if (typeof fallback === 'string') {
      return sanitize(fallback.replace(/^\s*current\s*headline\s*:\s*/i, ''));
    }
    return '';
  };

  // Try to resolve a profile image URL from results or sessionStorage
  const getProfileImageUrl = () => {
    const candidates = [];
    // from results if present
    try {
      candidates.push(
        results?.profileImageUrl,
        results?.avatar,
        results?.profile_picture_url,
        results?.profile?.imageUrl,
        results?.profile?.picture,
        results?.profile?.avatar,
        results?.profile?.photoUrl,
        results?.profile?.displayImageUrl
      );
    } catch (_) {}
    // from sessionStorage scraped payload - USE ONLY THE PROFILEURL PROP, NOT OLD CACHE
    try {
      // CRITICAL FIX: Always use profileUrl prop, never sessionStorage currentProfileUrl
      const currentUrl = profileUrl || '';
      const urlKey = btoa(currentUrl).replace(/[^a-zA-Z0-9]/g, '');
      const raw = sessionStorage.getItem(`scrapedProfileData_${urlKey}`);
      if (raw) {
        const parsed = normalizeScrapedData(JSON.parse(raw));
        candidates.push(
          parsed?.profileImageUrl,
          parsed?.imageUrl,
          parsed?.picture,
          parsed?.avatar,
          parsed?.profile_picture_url,
          parsed?.basic_info?.profile_picture_url,
          parsed?.photoUrl,
          parsed?.displayImage,
          parsed?.displayImageUrl,
          parsed?.profilePicture
        );
      } else {
        console.warn('⚠️ [Dashboard] No scraped data found for key:', `scrapedProfileData_${urlKey}`);
      }
    } catch (_) {}
    return candidates.find((x) => typeof x === 'string' && x.trim().length > 0);
  };

  const confirmExit = () => {
    setShowExitModal(false);
    onBackToHero && onBackToHero();
  };

  const cancelExit = () => {
    setShowExitModal(false);
  };

  // Toggle inspiration sections
  const toggleProfileInspirations = () => setShowProfileInspirations(prev => !prev);
  const toggleBannerInspirations = () => setShowBannerInspirations(prev => !prev);

  // Inspiration examples data
  const inspirationExamples = {
    profilePicture: [
      {
        image: '/inspiration/profile-1.jpg',
        title: 'Confident & Approachable',
        description: 'Bold colored background creates visual impact. Direct eye contact with confident expression builds instant trust.',
        tips: ['Use a solid or gradient colored background', 'Direct eye contact shows confidence', 'Professional yet approachable expression']
      },
      {
        image: '/inspiration/profile-2.jpg',
        title: 'Warm & Professional',
        description: 'Clean light background with genuine warm smile. Face fills the frame perfectly, creating connection.',
        tips: ['Light neutral background works great', 'Genuine smile creates trust', 'Face should fill 60% of frame']
      },
      {
        image: '/inspiration/profile-3.jpg',
        title: 'Creative & Memorable',
        description: 'Vibrant gradient background makes profile stand out. Glasses and smart-casual style show personality.',
        tips: ['Bold backgrounds help you stand out', 'Show your authentic personality', 'Professional attire matches your brand']
      }
    ],
    banner: [
      {
        image: '/inspiration/banner-1.jpg',
        title: 'Value Proposition + Social Proof',
        description: 'Clear "I HELP [WHO] + [WHAT]" statement with trusted brand logos. Action imagery creates energy.',
        tips: ['State WHO you help + HOW clearly', 'Add brand logos for credibility', 'Use engaging action imagery']
      },
      {
        image: '/inspiration/banner-2.jpg',
        title: 'Personal Brand Statement',
        description: 'Personal photo + clear value message + credentials. "As Seen In" logos build immediate trust.',
        tips: ['Include your photo for recognition', 'One bold value statement', 'Feature press/media logos']
      },
      {
        image: '/inspiration/banner-3.jpg',
        title: 'Simple CTA Banner',
        description: 'Minimal design with clear call-to-action. High contrast text ensures readability on any device.',
        tips: ['Keep it simple and bold', 'Use high contrast colors', 'Clear call-to-action text']
      }
    ]
  };

  // Close on Esc key
  useEffect(() => {
    if (!showExitModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowExitModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showExitModal]);

  // Scroll progress tracking for navigation bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
      
      // Determine active section based on scroll position
      const sectionKeys = ['profilePicture', 'banner', 'headline', 'about', 'profileUrl', 'toneAndCohesion', 'recommendations', 'experience', 'honorsAndAwards', 'educationAndCertification', 'skills', 'featured'];
      for (let i = sectionKeys.length - 1; i >= 0; i--) {
        const el = document.getElementById(`section-${sectionKeys[i]}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(sectionKeys[i]);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation section config - clean labels, no emojis
  const navSections = [
    { key: 'profilePicture', label: 'Photo' },
    { key: 'banner', label: 'Banner' },
    { key: 'headline', label: 'Headline' },
    { key: 'about', label: 'About' },
    { key: 'profileUrl', label: 'URL' },
    { key: 'toneAndCohesion', label: 'Tone' },
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'experience', label: 'Experience' },
    { key: 'honorsAndAwards', label: 'Awards' },
    { key: 'educationAndCertification', label: 'Education' },
    { key: 'skills', label: 'Skills' },
    { key: 'featured', label: 'Featured' }
  ];

  const scrollToSection = (sectionKey) => {
    const el = document.getElementById(`section-${sectionKey}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowMobileNav(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return { color: '#10b981', bg: '#dcfce7', class: 'progress-excellent' };
    if (score >= 70) return { color: '#3b82f6', bg: '#dbeafe', class: 'progress-good' };
    if (score >= 60) return { color: '#f59e0b', bg: '#fef3c7', class: 'progress-fair' };
    return { color: '#ef4444', bg: '#fee2e2', class: 'progress-poor' };
  };

  // Section scores are on a 1-10 scale; color code as:
  // 8-10: Green, 5-7: Yellow, 1-4: Red
  const getSectionScoreColor = (score) => {
    const s = Number(score) || 0;
    if (s >= 8) return { color: '#10b981', bg: '#dcfce7', class: 'progress-excellent' };
    if (s >= 5) return { color: '#f59e0b', bg: '#fef3c7', class: 'progress-fair' };
    return { color: '#ef4444', bg: '#fee2e2', class: 'progress-poor' };
  };

  // Priority helpers
  const getPriorityFromScore = (score) => {
    const s = Number(score);
    if (isNaN(s)) return 'low';
    if (s >= 8) return 'low';
    if (s >= 5) return 'medium';
    return 'high';
  };
  const passesPriority = (priority) => priorityFilter === 'all' || priority === priorityFilter;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(label);
      setTimeout(() => setCopiedText(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Collapsible long-text renderer
  const ReadMoreText = ({ text, limit = 500, style }) => {
    const [expanded, setExpanded] = React.useState(false);
    const t = String(text || '');
    if (!t) return null;
    if (t.length <= limit) {
      return <span style={style}>{t}</span>;
    }
    const shown = expanded ? t : `${t.slice(0, limit)}...`;
    return (
      <span style={style}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shown}</span>{' '}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#2563eb',
            cursor: 'pointer',
            padding: 0,
            fontSize: '0.9rem'
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  // Reusable TruncateText component with Read more / Read less toggle
  const TruncateText = ({ text, limit = 500 }) => {
    const [expanded, setExpanded] = React.useState(false);
    const str = typeof text === 'string' ? text : (text == null ? '' : String(text));
    if (str.length <= limit) return <span>{str}</span>;
    const shown = expanded ? str : `${str.slice(0, limit)}...`;
    return (
      <span>
        {shown}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="btn btn-link"
          style={{ padding: 0, marginLeft: '0.35rem', color: '#2563eb' }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  // Tabs removed

  // Priority Filter control with instant hover tooltip
  const PriorityFilter = () => {
    const [showTip, setShowTip] = React.useState(false);
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <div
          style={{
            position: 'relative',
            display: 'inline-flex',
            background: 'white',
            borderRadius: 9999,
            padding: 6,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            overflow: 'visible'
          }}
          title="Sort by priority"
          onMouseEnter={() => setShowTip(true)}
          onMouseLeave={() => setShowTip(false)}
        >
          {showTip && (
            <div style={{
              position: 'absolute',
              top: -40,
              right: 0,
              background: 'rgba(15,23,42,0.95)',
              color: '#fff',
              padding: '6px 8px',
              borderRadius: 6,
              fontSize: 12,
              whiteSpace: 'nowrap',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              pointerEvents: 'none',
              zIndex: 9999
            }}>
              Sort by priority
            </div>
          )}
          {['all','high','medium','low'].map((key) => {
            const isActive = priorityFilter === key;
            const palette = {
              all: { bg: isActive ? '#0ea5e9' : 'transparent', fg: isActive ? 'white' : '#0ea5e9', dot: '#0ea5e9' },
              high: { bg: isActive ? 'rgba(239, 68, 68, 0.1)' : 'transparent', fg: '#ef4444', dot: '#ef4444' },
              medium: { bg: isActive ? 'rgba(245, 158, 11, 0.1)' : 'transparent', fg: '#f59e0b', dot: '#f59e0b' },
              low: { bg: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent', fg: '#3b82f6', dot: '#3b82f6' }
            };
            return (
              <button
                key={key}
                onClick={() => setPriorityFilter(key)}
                aria-pressed={isActive}
                title="Sort by priority"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 9999,
                  border: 'none',
                  cursor: 'pointer',
                  background: palette[key]?.bg,
                  color: palette[key]?.fg,
                  transition: 'all 0.15s ease',
                  boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06) inset' : 'none'
                }}
              >
                {key !== 'all' && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: palette[key].dot,
                      boxShadow: '0 0 0 2px rgba(0,0,0,0.04)'
                    }}
                  />
                )}
                {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleDownloadClick = async () => {
    // If user has already paid this visit, download directly
    if (isFullAccessUnlocked) {
      console.log('✅ [DOWNLOAD] User already paid, downloading directly');
      await handleDownloadReport();
      return;
    }
    
    console.log('💳 [PAYMENT] Showing payment modal before download');
    
    // Scroll to top of page smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Wait for scroll animation to complete before initiating payment
    setTimeout(() => {
      setPaymentTriggerSource('download');
      initiateRazorpayPayment('download');
    }, 500);
  };

  const handleUnlockSuggestionsClick = () => {
    // If user has already paid this visit, no need to pay again
    if (isFullAccessUnlocked) {
      console.log('✅ [SUGGESTIONS] User already paid, suggestions already unlocked');
      return;
    }
    
    console.log('💳 [PAYMENT] Showing payment modal to unlock suggestions');
    
    // Scroll to top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    setTimeout(() => {
      setPaymentTriggerSource('suggestions');
      initiateRazorpayPayment('suggestions');
    }, 500);
  };

  // NOTE: Report email is now sent entirely server-side, triggered from
  // api/verify-razorpay-payment.js after payment verification. The
  // previous client-side sendReportEmail() was removed on 2026-04-21
  // because it was losing emails whenever a user closed the tab before
  // the ~45–60s PDF-render + SMTP-upload chain finished. See
  // api/send-audit-report.js for the server-side path.

  const initiateRazorpayPayment = async (source) => {
    if (!user?.email) {
      alert('Please login to continue with payment');
      return;
    }

    setIsProcessingPayment(true);

    try {
      await razorpayService.initiatePayment({
        userEmail: user.email,
        userName: user.displayName || '',
        profileUrl: profileUrl,
        triggerSource: source,
        onSuccess: async (response) => {
          console.log('✅ [PAYMENT] Payment successful!', response.razorpay_payment_id);
          setIsFullAccessUnlocked(true);
          setIsProcessingPayment(false);

          // Report email is dispatched server-side by
          // api/verify-razorpay-payment.js → api/send-audit-report.js.
          // Nothing to do here except unlock the UI.

          // If triggered from download button, proceed with the
          // in-browser PDF download flow (separate from email).
          if (source === 'download') {
            await handleDownloadReport();
          }
        },
        onFailure: (error) => {
          console.error('❌ [PAYMENT] Payment failed:', error);
          setIsProcessingPayment(false);
          // User can simply try again by clicking the button
        },
        onCancel: () => {
          console.log('⚠️ [PAYMENT] Payment cancelled by user');
          setIsProcessingPayment(false);
          // User can simply try again by clicking the button
        }
      });
    } catch (error) {
      console.error('❌ [PAYMENT] Payment initiation error:', error);
      setIsProcessingPayment(false);
      // User can simply try again by clicking the button
    }
  };

  // Download Report as PDF
  const handleDownloadReport = async () => {
    try {
      track.pdfDownloaded({ source: paymentTriggerSource });
      setIsGeneratingPDF(true);
      
      // Wait a moment for UI to update
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Get the main dashboard content
      const dashboardElement = document.querySelector('.section.dashboard-fullscreen') || document.body;
      
      // Get user's name for filename
      const userName = questionnaireData?.fullName || 'Professional';

      
      // Generate PDF with optimized settings for smaller file size
      // Reduced scale from 2 to 1.2 for better file size (~10MB instead of ~200MB)
      const canvas = await html2canvas(dashboardElement, {
        scale: 1.2, // Reduced from 2 to 1.2 for smaller file size while maintaining quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          // Prevent document.write warning
          clonedDoc.write = () => {};
          
          // Hide download button in the cloned document
          const downloadBtn = clonedDoc.querySelector('button[disabled]');
          if (downloadBtn) downloadBtn.style.display = 'none';
        }
      });

      // A4 dimensions in mm
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Use JPEG with 0.85 quality instead of PNG for much smaller file size
      // JPEG compression reduces file size by ~70-80% with minimal quality loss
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;

      // Add new pages if content exceeds one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }

      // Save PDF
      const fileName = `LinkedIn_Profile_Report_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      
    } catch (error) {
      console.error('❌ [PDF] Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Friendly labels for the 10 sub-score keys (used by StrengthsBreakdown).
  // The order here is also the tie-break order when scores are equal.
  const SECTION_LABELS = {
    profilePicture: 'Profile picture',
    headline: 'Headline',
    about: 'About section',
    experience: 'Experience',
    toneAndCohesion: 'Tone & cohesion',
    recommendations: 'Recommendations',
    banner: 'Banner',
    profileUrl: 'Profile URL',
    honorsAndAwards: 'Honors & awards',
    educationAndCertification: 'Education',
  };

  // Strengths-First Tier: lead with 3 things doing well, then 3 locked weak
  // areas with a quantified "lift potential" claim. Replaces the cold
  // 47/100-style framing as users see the score for the first time.
  // Reframe rationale: 0% of audited profiles have ever scored 80+ — the
  // "/100" framing makes everyone feel below average. Showing strengths
  // first reverses the emotional arc before the paywall.
  const StrengthsBreakdown = () => {
    if (!results?.sections) return null;
    const entries = Object.entries(results.sections)
      .filter(([key, sec]) => SECTION_LABELS[key] && typeof sec?.score === 'number')
      .map(([key, sec]) => ({ key, label: SECTION_LABELS[key], score: sec.score }));
    if (entries.length < 6) return null;

    const sorted = [...entries].sort((a, b) => b.score - a.score);
    const strengths = sorted.slice(0, 3);
    const weaknesses = sorted.slice(-3).reverse(); // weakest first

    // Lift potential: how many points you'd gain if each weak section moved to 9/10.
    const liftPotential = weaknesses.reduce((acc, w) => acc + Math.max(0, 9 - w.score), 0);

    return (
      <div style={{
        marginBottom: '2rem',
        background: 'white',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
      }}>
        {/* Strengths */}
        <div style={{ padding: '24px 28px 20px', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            ✓ 3 things your profile already does well
          </div>
          {strengths.map((s) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
              <CheckCircle size={18} color="#16a34a" />
              <span style={{ flex: 1, color: '#0f172a', fontSize: 15 }}>{s.label}</span>
              <span style={{ fontWeight: 600, color: '#15803d', fontSize: 14 }}>{s.score} / 10</span>
            </div>
          ))}
        </div>

        {/* Weaknesses (locked behind paywall) */}
        <div style={{ padding: '20px 28px 24px', background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            ✗ 3 things costing you opportunities
          </div>
          {weaknesses.map((w) => (
            <div key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', opacity: isFullAccessUnlocked ? 1 : 0.85 }}>
              <Lock size={18} color="#dc2626" />
              <span style={{ flex: 1, color: '#0f172a', fontSize: 15 }}>{w.label}</span>
              <span style={{
                fontWeight: 600,
                fontSize: 14,
                color: isFullAccessUnlocked ? '#b91c1c' : '#94a3b8',
                filter: isFullAccessUnlocked ? 'none' : 'blur(4px)',
              }}>
                {isFullAccessUnlocked ? `${w.score} / 10` : '? / 10'}
              </span>
            </div>
          ))}

          {!isFullAccessUnlocked && liftPotential >= 6 && (
            <div style={{
              marginTop: 18,
              padding: '14px 16px',
              background: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 10,
              border: '1px dashed #fca5a5',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>
                These 3 fixes alone could lift you <strong style={{ color: '#0f172a' }}>{liftPotential}+ points</strong>.
              </div>
              <button
                onClick={handleUnlockSuggestionsClick}
                style={{
                  background: '#0f172a',
                  color: 'white',
                  border: 'none',
                  borderRadius: 999,
                  padding: '12px 24px',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                See the {weaknesses.length} fixes
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Reusable Score Hero shown on all tabs
  const ScoreHero = () => (
    <div className="card" style={{ textAlign: 'center', marginBottom: '2rem', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <Trophy style={{ color: getScoreColor(results.overallScore).color, width: '48px', height: '48px' }} />
        <div>
          <h2 style={{ fontSize: '3rem', fontWeight: '700', color: getScoreColor(results.overallScore).color, margin: 0 }}>
            {results.overallScore}/{results.maxScore}
          </h2>
          <p style={{ color: '#64748b', margin: 0 }}>LinkedIn Optimization Score</p>
        </div>
      </div>
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <div className="progress-container">
          <div 
            className={`progress-bar ${getScoreColor(results.overallScore).class}`}
            style={{ width: `${(results.overallScore / results.maxScore) * 100}%` }}
          />
        </div>
      </div>
      <p style={{ color: '#64748b', marginTop: '1rem', fontSize: '1.1rem', fontWeight: '500' }}>
      {results.overallScore >= 100 ? '🌟 Perfect profile. You\'ve nailed every detail.' :
   results.overallScore >= 90 ? '🏆 Excellent profile. Just polish the small things.' :
   results.overallScore >= 80 ? '🎯 Strong profile. Clear and confident, almost top tier.' :
   results.overallScore >= 70 ? '📈 Good profile. Solid effort, fine-tuning will lift it higher.' :
   results.overallScore >= 60 ? '💡 Decent profile. Some parts shine, others need attention.' :
   results.overallScore >= 50 ? '🚀 Average profile. Visible progress but gaps hold it back.' :
   results.overallScore >= 40 ? '🔎 Basic profile. Foundation is there, depth is missing.' :
   results.overallScore >= 30 ? '⚡ Early profile. Rough edges, but potential shows.' :
   results.overallScore >= 20 ? '🛠️ Weak profile. Needs more detail and structure.' :
   results.overallScore >= 10 ? '📉 Very poor profile. Only bare minimum added.' :
   '❌ No profile. Start building from scratch.'}
      </p>
    </div>
  );

  const FeedbackTab = () => {

    return (
    <div>
      {/* Score hero visible on this tab */}
      <ScoreHero />

      {/* Strengths-First Tier reframe — sits immediately after the score so
          users see what they're doing well BEFORE the deflating raw number
          sinks in. Drives the paywall click via quantified lift potential. */}
      <StrengthsBreakdown />

      {/* LinkedIn Profile Parameters */}
      <div style={{ marginBottom: '3rem' }}>
        {/* Controls Container - Download Button + SSI Button + Filter */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1rem',
          flexWrap: 'wrap'
        }}>
          {/* Left Side Buttons */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* SSI Score Button */}
          <a
            href="https://www.linkedin.com/sales/ssi?src=or-search&trk=lss-microsite_ssi-score-entry-page_get-your-ssi-score-button&veh=www.google.com%7Cor-search"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 119, 181, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 119, 181, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
            }}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #0077B5 0%, #6366f1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 8px 24px rgba(0, 119, 181, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
              overflow: 'hidden'
            }}
          >
            {/* Shimmer Effect */}
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: '-200%',
                width: '200%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                animation: 'shimmer 3s infinite',
                pointerEvents: 'none'
              }}
            />
            
            {/* Icon */}
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <TrendingUp style={{ width: 18, height: 18, color: 'white' }} />
            </span>
            
            <span style={{ position: 'relative', zIndex: 1 }}>
              SSI Score
            </span>
          </a>
          
          {/* Download Report Button - Beside SSI Score */}
          <button
            onClick={handleDownloadClick}
            disabled={isGeneratingPDF}
            onMouseEnter={(e) => {
              if (!isGeneratingPDF) {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
            }}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 28px',
              background: isGeneratingPDF 
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
              overflow: 'hidden',
              opacity: isGeneratingPDF ? 0.7 : 1
            }}
          >
            {/* Shimmer Effect */}
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: '-200%',
                width: '200%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                animation: 'shimmer 3s infinite',
                pointerEvents: 'none'
              }}
            />
            
            {/* Icon */}
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Download style={{ width: 18, height: 18, color: 'white' }} />
            </span>
            
            <span style={{ position: 'relative', zIndex: 1 }}>
              {isGeneratingPDF ? 'Generating...' : 'Download Report'}
            </span>
          </button>
          </div>

          {/* Priority Filter - Right Side */}
          <PriorityFilter />
        </div>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {(() => {
            const orderedKeys = [
              'profilePicture',
              'banner', 
              'headline',
              'about',
              'profileUrl',
              'toneAndCohesion',
              'recommendations',
              'experience',
              'honorsAndAwards',
              'educationAndCertification'
            ];
            const keysToRender = orderedKeys.filter((k) => results.sections && results.sections[k]);
            return keysToRender
              .filter((k) => {
                const sec = results.sections[k] || {};
                const pr = getPriorityFromScore(sec.score);
                return passesPriority(pr);
              })
              .map((sectionKey) => {
                const data = results.sections[sectionKey];
                const scoreData = getSectionScoreColor(data.score);
                const sectionNames = {
                  profilePicture: 'Profile Picture',
                  banner: 'Banner Image',
                  headline: 'Headline',
                  about: 'About Section',
                  profileUrl: 'Profile URL',
                  toneAndCohesion: 'Tone & Cohesion',
                  recommendations: 'Recommendations',
                  experience: 'Experience',
                  honorsAndAwards: 'Honors & Awards',
                  educationAndCertification: 'Education & Certification'
                };
                const sectionIcons = {
                  profilePicture: <Camera style={{ width: 18, height: 18, color: '#64748b' }} />,
                  banner: <Image style={{ width: 18, height: 18, color: '#64748b' }} />,
                  headline: <Type style={{ width: 18, height: 18, color: '#64748b' }} />,
                  about: <AlignLeft style={{ width: 18, height: 18, color: '#64748b' }} />,
                  profileUrl: <Link2 style={{ width: 18, height: 18, color: '#64748b' }} />,
                  toneAndCohesion: <Palette style={{ width: 18, height: 18, color: '#64748b' }} />,
                  recommendations: <ThumbsUp style={{ width: 18, height: 18, color: '#64748b' }} />,
                  experience: <Briefcase style={{ width: 18, height: 18, color: '#64748b' }} />,
                  honorsAndAwards: <Award style={{ width: 18, height: 18, color: '#64748b' }} />,
                  educationAndCertification: <GraduationCap style={{ width: 18, height: 18, color: '#64748b' }} />
                };

                return (
                  <React.Fragment key={sectionKey}>
                  <div id={`section-${sectionKey}`} className="card" style={{ background: 'rgba(255, 255, 255, 0.95)', scrollMarginTop: '70px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                      <div>
                        <h4 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span aria-hidden="true" style={{ display: 'inline-flex' }}>
                            {sectionIcons[sectionKey]}
                          </span>
                          {sectionNames[sectionKey]}
                        </h4>
                        {/* Priority badge removed as requested */}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '2.5rem', fontWeight: '700', color: scoreData.color }}>
                          {data.score}/{data.maxScore}
                        </div>
                        <div className="progress-container" style={{ width: '80px', margin: '0.5rem 0' }}>
                          <div 
                            className={`progress-bar ${scoreData.class}`}
                            style={{ width: `${(data.score / data.maxScore) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Current vs Optimized */}
                    <div className="grid grid-2" style={{ gap: '1.5rem' }}>
                      {/* Current */}
                      <div style={{ 
                        padding: '1.5rem', 
                        background: 'rgba(248, 250, 252, 0.8)', 
                        borderRadius: '12px',
                        border: '1px solid rgba(226, 232, 240, 0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '400px',
                        position: 'relative'
                      }}>
                        <h5 style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          color: '#7c3aed', 
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexShrink: 0
                        }}>
                          <FileText style={{ width: 18, height: 18, color: '#7c3aed' }} />
                          Current Status
                        </h5>
                        <div 
                          className="scroll-container"
                          style={{ 
                            flex: 1, 
                            overflowY: 'auto', 
                            paddingRight: '0.5rem',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(124, 58, 237, 0.4) rgba(248, 250, 252, 0.5)'
                          }}
                        >
                        {sectionKey === 'profilePicture' ? (
                          (() => {
                            const imgUrl = getProfileImageUrl();
                            const link = imgUrl ? (
                              <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                                <a href={imgUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem' }}>
                                  Open full image ↗
                                </a>
                              </div>
                            ) : null;
                            return imgUrl ? (
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                  <img
                                    src={proxyImage(imgUrl)}
                                    alt="Profile"
                                    style={{
                                      width: 120,
                                      height: 120,
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '3px solid #e2e8f0',
                                      boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
                                    }}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                </div>
                                {link && (
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '0.75rem' }}>
                                    <a href={imgUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem' }}>Open full image ↗</a>
                                  </div>
                                )}
                                
                                {/* AI Profile Picture Generator - Minimal Modern Design */}
                                <a
                                  href="https://tools.growleads.io/linkedin-profile-picture-generator"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'block',
                                    marginTop: '20px',
                                    padding: '20px',
                                    background: '#fff',
                                    borderRadius: '14px',
                                    border: '1px solid #e2e8f0',
                                    textDecoration: 'none',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#8b5cf6';
                                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(139, 92, 246, 0.12)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    {/* Icon Container */}
                                    <div style={{
                                      width: '48px',
                                      height: '48px',
                                      borderRadius: '12px',
                                      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <img
                                        src="/profile-tool-trans.png"
                                        alt="LinkedIn Profile Picture Generator"
                                        style={{
                                          width: 28,
                                          height: 28,
                                          objectFit: 'contain',
                                          display: 'block'
                                        }}
                                        loading="lazy"
                                      />
                                    </div>
                                    
                                    {/* Content */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <span style={{
                                          fontSize: '0.95rem',
                                          fontWeight: 700,
                                          color: '#1e293b',
                                          letterSpacing: '-0.01em'
                                        }}>
                                          Linkedin Headshot Generator
                                        </span>
                                        <span style={{
                                          padding: '2px 8px',
                                          background: '#dcfce7',
                                          color: '#15803d',
                                          fontSize: '0.65rem',
                                          fontWeight: 600,
                                          borderRadius: '4px',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px'
                                        }}>
                                          Free
                                        </span>
                                      </div>
                                      <p style={{
                                        margin: 0,
                                        fontSize: '0.82rem',
                                        color: '#64748b',
                                        lineHeight: 1.4
                                      }}>
                                        Create a professional profile picture with AI in just few seconds
                                      </p>
                                    </div>
                                    
                                    {/* Arrow */}
                                    <div style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '8px',
                                      background: '#f8fafc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M7 17L17 7M17 7H7M17 7V17"/>
                                      </svg>
                                    </div>
                                  </div>
                                </a>

                                {/* Inline Profile Picture Inspirations */}
                                {showProfileInspirations && (
                                  <div style={{
                                    marginTop: '16px',
                                    padding: '20px',
                                    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(139, 92, 246, 0.2)'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                      <Sparkles style={{ width: 18, height: 18, color: '#7c3aed' }} />
                                      <h6 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#6b21a8' }}>
                                        Profile Picture Inspiration
                                      </h6>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                      {inspirationExamples.profilePicture.map((example, idx) => (
                                        <div key={idx} style={{
                                          background: 'white',
                                          borderRadius: '10px',
                                          overflow: 'hidden',
                                          border: '1px solid rgba(226, 232, 240, 0.8)',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                        }}>
                                          <div style={{ position: 'relative' }}>
                                            <img
                                              src={example.image}
                                              alt={example.title}
                                              style={{
                                                width: '100%',
                                                aspectRatio: '1',
                                                objectFit: 'cover'
                                              }}
                                            />
                                          </div>
                                          <div style={{ padding: '10px' }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                                              {example.title}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.4 }}>
                                              {example.tips[0]}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div style={{
                                      marginTop: '12px',
                                      padding: '10px 12px',
                                      background: 'rgba(139, 92, 246, 0.1)',
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'start',
                                      gap: '8px'
                                    }}>
                                      <CheckCircle style={{ width: 14, height: 14, color: '#7c3aed', flexShrink: 0, marginTop: 2 }} />
                                      <span style={{ fontSize: '0.75rem', color: '#6b21a8', lineHeight: 1.4 }}>
                                        <strong>Pro Tip:</strong> Face should fill 60% of frame. Use natural lighting and ensure direct eye contact for maximum impact.
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null;
                          })()
                        ) : sectionKey === 'headline' ? (
                          <div>
                            <p style={{ 
                              color: '#64748b', 
                              fontSize: '0.9rem', 
                              lineHeight: '1.5',
                              margin: 0
                            }}>
                              {getHeadlineText(data.current)}
                            </p>
                            
                            {/* AI Headline Generator - Minimal Modern Design */}
                            <a
                              href="https://tools.growleads.io/linkedin-headline-generator"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'block',
                                marginTop: '20px',
                                padding: '20px',
                                background: '#fff',
                                borderRadius: '14px',
                                border: '1px solid #e2e8f0',
                                textDecoration: 'none',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#f59e0b';
                                e.currentTarget.style.boxShadow = '0 8px 30px rgba(245, 158, 11, 0.12)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {/* Icon Container */}
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '12px',
                                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <img
                                    src="/headline-tool-tans.png"
                                    alt="LinkedIn Headline Generator"
                                    style={{
                                      width: 28,
                                      height: 28,
                                      objectFit: 'contain',
                                      display: 'block'
                                    }}
                                    loading="lazy"
                                  />
                                </div>
                                
                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{
                                      fontSize: '0.95rem',
                                      fontWeight: 700,
                                      color: '#1e293b',
                                      letterSpacing: '-0.01em'
                                    }}>
                                      Linkedin Headline Generator
                                    </span>
                                    <span style={{
                                      padding: '2px 8px',
                                      background: '#dcfce7',
                                      color: '#15803d',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      borderRadius: '4px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.3px'
                                    }}>
                                      Free
                                    </span>
                                  </div>
                                  <p style={{
                                    margin: 0,
                                    fontSize: '0.82rem',
                                    color: '#64748b',
                                    lineHeight: 1.4
                                  }}>
                                    Create Search optimized, attention-grabbing headlines that position you as an authority
                                  </p>
                                </div>
                                
                                {/* Arrow */}
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  background: '#f8fafc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                                  </svg>
                                </div>
                              </div>
                            </a>
                          </div>
                        ) : sectionKey === 'experience' ? (
                          (() => {
                            const exps = getExperienceData();
                            if (!exps.length) return (
                              <p style={{ color: '#64748b', margin: 0 }}>No experience entries found.</p>
                            );
                            
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {exps.map((exp, idx) => (
                                  <div
                                    key={`exp-${idx}`}
                                    style={{
                                      border: '1px solid rgba(226, 232, 240, 0.9)',
                                      borderRadius: 12,
                                      background: '#ffffff',
                                      boxShadow: '0 2px 10px rgba(15,23,42,0.04)',
                                      padding: '12px'
                                    }}
                                  >
                                    <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr', gap: '12px', alignItems: 'start' }}>
                                      <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                                        {exp.company_logo_url ? (
                                          <img src={proxyImage(exp.company_logo_url)} alt="Company" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                                        ) : null}
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{exp.title || 'Role'}</div>
                                        <div style={{ color: '#334155' }}>{[exp.company, exp.location].filter(Boolean).join(' · ')}</div>
                                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{formatExperienceDateRange(exp)}</div>
                                        {exp.description && (
                                          <div style={{ color: '#475569', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                                            <ReadMoreText text={exp.description} limit={500} />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()
                        ) : sectionKey === 'banner' ? (
                          (() => {
                            const bannerUrl = getBannerImageUrl();
                            const link = bannerUrl ? (
                              <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                                <a href={bannerUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem' }}>
                                  Open full image ↗
                                </a>
                              </div>
                            ) : null;
                            const bannerImageFailed = bannerUrl && failedBannerImageUrls.has(bannerUrl);
                            
                            if (!bannerUrl || bannerImageFailed) {
                              return (
                                <div style={{ padding: '12px', color: '#64748b', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                  {bannerImageFailed
                                    ? 'LinkedIn blocked this banner image preview. The audit still detected a custom banner.'
                                    : (data?.current || 'No banner image detected.')}
                                  {link}
                                </div>
                              );
                            }
                            
                            return (
                              <div style={{
                                width: '100%',
                                borderRadius: '12px',
                                border: '1px solid rgba(226, 232, 240, 0.7)',
                                background: '#fff',
                                padding: '8px'
                              }}>
                                <div style={{
                                  position: 'relative',
                                  width: '100%',
                                  // 4:1 aspect ratio (LinkedIn recommended 1584x396)
                                  paddingTop: '25%'
                                }}>
                                  <img
                                    src={proxyImage(bannerUrl)}
                                    alt="Banner"
                                    style={{ 
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      objectPosition: 'center',
                                      borderRadius: '8px'
                                    }}
                                    onError={(e) => { 
                                      e.currentTarget.style.display = 'none';
                                      markBannerImageFailed(bannerUrl);
                                    }}
                                  />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '0.75rem' }}>
                                  <a href={bannerUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem' }}>Open full image ↗</a>
                                </div>
                                
                                {/* Inline Banner Inspirations */}
                                {showBannerInspirations && (
                                  <div style={{
                                    marginTop: '16px',
                                    padding: '20px',
                                    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(139, 92, 246, 0.2)'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                      <Sparkles style={{ width: 18, height: 18, color: '#7c3aed' }} />
                                      <h6 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#6b21a8' }}>
                                        Banner Image Inspiration
                                      </h6>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                      {inspirationExamples.banner.map((example, idx) => (
                                        <div key={idx} style={{
                                          background: 'white',
                                          borderRadius: '12px',
                                          overflow: 'hidden',
                                          border: '1px solid rgba(226, 232, 240, 0.8)',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                        }}>
                                          <div style={{ position: 'relative' }}>
                                            <img
                                              src={example.image}
                                              alt={example.title}
                                              style={{
                                                width: '100%',
                                                aspectRatio: '4/1',
                                                objectFit: 'cover'
                                              }}
                                            />
                                          </div>
                                          <div style={{ padding: '12px 16px' }}>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                                              {example.title}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '8px' }}>
                                              {example.description}
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                              {example.tips.map((tip, tipIdx) => (
                                                <span key={tipIdx} style={{
                                                  fontSize: '0.7rem',
                                                  padding: '3px 8px',
                                                  background: 'rgba(139, 92, 246, 0.1)',
                                                  color: '#7c3aed',
                                                  borderRadius: '4px',
                                                  fontWeight: 500
                                                }}>
                                                  ✓ {tip}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div style={{
                                      marginTop: '16px',
                                      padding: '12px 14px',
                                      background: 'rgba(139, 92, 246, 0.1)',
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'start',
                                      gap: '8px'
                                    }}>
                                      <CheckCircle style={{ width: 14, height: 14, color: '#7c3aed', flexShrink: 0, marginTop: 2 }} />
                                      <span style={{ fontSize: '0.8rem', color: '#6b21a8', lineHeight: 1.4 }}>
                                        <strong>Pro Tip:</strong> Use 1584×396px dimensions. Keep important text in the left-center safe zone (not covered by profile photo).
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* LinkedIn Banner Generator - Lead Magnet */}
                                <a
                                  href="https://tools.growleads.io/linkedin-banner-generator"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'block',
                                    marginTop: '16px',
                                    padding: '16px',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(96, 165, 250, 0.3)',
                                    textDecoration: 'none',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 8px rgba(96, 165, 250, 0.1)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(96, 165, 250, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <div style={{
                                      width: 44,
                                      height: 44,
                                      borderRadius: '10px',
                                      overflow: 'hidden',
                                      background: '#f8fafc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <img
                                        src="/Banner trans.png"
                                        alt="LinkedIn Banner Generator"
                                        style={{
                                          width: 36,
                                          height: 36,
                                          objectFit: 'contain'
                                        }}
                                      />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <span style={{
                                          fontSize: '0.95rem',
                                          fontWeight: 700,
                                          color: '#1e293b',
                                          letterSpacing: '-0.01em'
                                        }}>
                                          LinkedIn Banner Generator
                                        </span>
                                        <span style={{
                                          padding: '2px 8px',
                                          background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                                          color: '#fff',
                                          fontSize: '0.65rem',
                                          fontWeight: 700,
                                          borderRadius: '4px',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Free Tool
                                        </span>
                                      </div>
                                      <p style={{
                                        margin: 0,
                                        fontSize: '0.8rem',
                                        color: '#64748b',
                                        lineHeight: 1.4
                                      }}>
                                        Create stunning, professional LinkedIn banners that capture attention and showcase your brand
                                      </p>
                                    </div>
                                    <div style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '8px',
                                      background: '#f8fafc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M7 17L17 7M17 7H7M17 7V17"/>
                                      </svg>
                                    </div>
                                  </div>
                                </a>
                              </div>
                            );
                          })()
                        ) : sectionKey === 'profileUrl' ? (
                          (() => {
                            const url = profileUrl || results?.url || results?.profile?.url || '';
                            return url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#2563eb',
                                  fontSize: '0.9rem',
                                  textDecoration: 'none',
                                  wordBreak: 'break-all'
                                }}
                              >
                                {url}
                              </a>
                            ) : (
                              <p style={{ color: '#64748b', margin: 0 }}>No profile URL available</p>
                            );
                          })()
                        ) : sectionKey === 'about' ? (
                          (() => {
                            const aboutText = getAboutText('');
                            if (!aboutText || !aboutText.trim()) {
                              return <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>Not found</p>;
                            }
                            return (
                              <div style={{ 
                                color: '#64748b', 
                                fontSize: '0.9rem', 
                                lineHeight: '1.7',
                                margin: 0,
                                whiteSpace: 'pre-wrap'
                              }}>
                                <ReadMoreText text={aboutText} limit={500} />
                              </div>
                            );
                          })()
                        ) : sectionKey === 'recommendations' ? (
                          (() => {
                            const recs = getRecommendationsData();
                            const count = recs.length;
                            if (count === 0) {
                              return (
                                <p style={{ color: '#64748b', margin: 0 }}>
                                  No recommendations found on profile
                                </p>
                              );
                            }
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {recs.map((rec, idx) => {
                                  // Handle both object and string formats
                                  const isString = typeof rec === 'string';
                                  const recommenderName = isString ? 'Recommender' : (rec?.recommender_name || rec?.name || rec?.recommender || 'Recommender');
                                  const recommenderTitle = isString ? '' : (rec?.recommender_title || rec?.title || '');
                                  const relationship = isString ? '' : (rec?.relationship || '');
                                  const date = isString ? '' : (rec?.recommendation_date || rec?.date || rec?.timestamp || '');
                                  const recommendationText = isString ? rec : (rec?.recommendation_text || rec?.text || rec?.content || '');
                                  
                                  if (!recommendationText || !recommendationText.trim()) return null;
                                  
                                  return (
                                    <div 
                                      key={idx} 
                                      style={{ 
                                        padding: '16px', 
                                        border: '1px solid #e2e8f0', 
                                        borderRadius: '12px', 
                                        background: '#fafafa',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px'
                                      }}
                                    >
                                      {/* Recommender Info */}
                                      <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                                        <div style={{ 
                                          width: '48px', 
                                          height: '48px', 
                                          borderRadius: '50%', 
                                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                          color: '#fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '18px',
                                          fontWeight: '600',
                                          flexShrink: 0
                                        }}>
                                          {recommenderName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.95rem' }}>
                                            {recommenderName}
                                          </div>
                                          {recommenderTitle && (
                                            <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '2px' }}>
                                              {recommenderTitle}
                                            </div>
                                          )}
                                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                                            {relationship && (
                                              <span style={{ 
                                                fontSize: '0.8rem', 
                                                color: '#475569',
                                                background: '#f1f5f9',
                                                padding: '2px 8px',
                                                borderRadius: '4px'
                                              }}>
                                                {relationship}
                                              </span>
                                            )}
                                            {date && (
                                              <span style={{ 
                                                fontSize: '0.8rem', 
                                                color: '#64748b'
                                              }}>
                                                {date}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {/* Recommendation Text */}
                                      <div style={{ 
                                        color: '#334155', 
                                        fontSize: '0.92rem', 
                                        lineHeight: '1.6',
                                        paddingLeft: '60px'
                                      }}>
                                        <ReadMoreText text={recommendationText} limit={300} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : sectionKey === 'educationAndCertification' ? (
                          (() => {
                            const edus = getEducationData();
                            const certs = getCertificationsData();
                            if (!edus.length && !certs.length) return (
                              <p style={{ color: '#64748b', margin: 0 }}>No education or certifications found.</p>
                            );
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {edus.length > 0 && (
                                  <div>
                                    <h6 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '0.95rem' }}>Education</h6>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                      {edus.map((edu, idx) => (
                                        <div key={`edu-${idx}`} style={{ display: 'grid', gridTemplateColumns: '48px 1fr', gap: '12px', alignItems: 'start' }}>
                                          {(() => {
                                            // Try multiple logo field names - EXACT priority from docs
                                            const logoUrl = edu.school_logo_url || 
                                                           edu.institute_logo_url || 
                                                           edu.logo_url || 
                                                           edu.logo || 
                                                           edu.image_url || 
                                                           edu.school_logo || 
                                                           edu.organization_logo || 
                                                           '';
                                            
                                            // Generate initials for fallback - EXACT logic from docs
                                            const nameForInitials = edu.title || 
                                                                   edu.school || 
                                                                   edu.institution || 
                                                                   edu.organization || 
                                                                   '';
                                            
                                            const initials = String(nameForInitials)
                                              .split(/\s+/)
                                              .map(s => s?.[0])
                                              .filter(Boolean)
                                              .slice(0, 2)
                                              .join('')
                                              .toUpperCase();
                                            
                                            return (
                                              <div style={{ 
                                                width: 48, 
                                                height: 48, 
                                                borderRadius: 8, 
                                                overflow: 'hidden', 
                                                background: '#f1f5f9', 
                                                border: '1px solid #e2e8f0', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                color: '#64748b', 
                                                fontWeight: 700 
                                              }}>
                                                {logoUrl ? (
                                                  <img
                                                    src={proxyImage(logoUrl)}
                                                    alt="Institute"
                                                    style={{ 
                                                      width: '100%', 
                                                      height: '100%', 
                                                      objectFit: 'contain' 
                                                    }}
                                                    onError={(e) => { 
                                                      e.currentTarget.style.display = 'none'; 
                                                      e.currentTarget.parentElement.textContent = initials || '•'; 
                                                    }}
                                                  />
                                                ) : (
                                                  <span>{initials || '•'}</span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          <div>
                                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{edu.school || edu.title || 'Institution'}</div>
                                            <div style={{ color: '#334155' }}>
                                              {[edu.degree || edu.degree_name, edu.field_of_study].filter(Boolean).join(' · ')}
                                            </div>
                                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                              {formatEducationDateRange(edu)}
                                            </div>
                                            {edu.description && (
                                              <div style={{ color: '#475569', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                                                <ReadMoreText text={edu.description} limit={500} />
                                              </div>
                                            )}
                                            {(() => {
                                              const chips = [];
                                              if (edu.gpa) chips.push(['GPA', edu.gpa]);
                                              if (edu.grade) chips.push(['Grade', edu.grade]);
                                              if (edu.activities) chips.push(['Activities', edu.activities]);
                                              if (edu.societies || edu.society) chips.push(['Societies', edu.societies || edu.society]);
                                              if (edu.location) chips.push(['Location', edu.location]);
                                              const known = new Set(['title','school','institution','organization','degree','field_of_study','degree_field','start_year','start_month','end_year','end_month','startDate','endDate','description','institute_logo_url','logo_url','logo','image_url','school_logo','organization_logo','gpa','grade','activities','societies','society','location','url','website']);
                                              const extras = Object.entries(edu || {})
                                                .filter(([k, v]) => !known.has(k) && typeof v === 'string' && v.trim())
                                                .slice(0, 8);
                                              return (
                                                <>
                                                  {chips.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                                      {chips.map(([label, value]) => (
                                                        <div key={label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', minWidth: '120px' }}>
                                                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</div>
                                                          <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{value}</div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <h6 style={{ margin: '8px 0 16px', color: '#0f172a', fontSize: '1rem', fontWeight: 600 }}>Certifications</h6>
                                  {certs.length > 0 ? (
                                    <>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
                                        {certs.map((c, idx) => {
                                        const title = c.title || c.name || 'Certification';
                                        const issuer = c.subtitle || c.issuing_organization || c.issuer || '';
                                        const issue = c.issue_date || c.date || c.meta || '';
                                        const id = c.credential_id || c.id || '';
                                        const url = c.credential_url || c.url || '';
                                        return (
                                          <div key={`cert-${idx}`} style={{ padding: 16, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', boxShadow: '0 2px 10px rgba(15,23,42,0.04)' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 12, alignItems: 'start' }}>
                                              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fef3c7', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏅</div>
                                              <div>
                                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{title}</div>
                                                <div style={{ color: '#334155', fontSize: '0.92rem', marginTop: 2 }}>
                                                  {[issuer, issue].filter(Boolean).join(' · ')}
                                                </div>
                                                {c.description && (
                                                  <div style={{ color: '#475569', marginTop: 8, whiteSpace: 'pre-wrap', fontSize: '0.92rem' }}>
                                                    <ReadMoreText text={c.description} limit={500} />
                                                  </div>
                                                )}
                                                {id ? (
                                                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
                                                    <span style={{ fontSize: '0.85rem', background: '#f1f5f9', border: '1px dashed #e2e8f0', padding: '6px 8px', borderRadius: 6 }}>ID: {id}</span>
                                                    <button
                                                      type="button"
                                                      onClick={() => copyToClipboard(id, `cert-${idx}`)}
                                                      style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#334155', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' }}
                                                      aria-label={`Copy credential ID for ${title}`}
                                                    >
                                                      Copy
                                                    </button>
                                                    {copiedText === `cert-${idx}` && <span style={{ fontSize: 12, color: '#10b981' }}>Copied</span>}
                                                  </div>
                                                ) : null}
                                                {url ? (
                                                  <div style={{ marginTop: 10 }}>
                                                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.9rem', textDecoration: 'none' }}>Verify credential ↗</a>
                                                  </div>
                                                ) : null}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                        })}
                                      </div>
                                    </>
                                  ) : (
                                    <p style={{ color: '#64748b', margin: 0 }}>No certifications found.</p>
                                  )}
                                </div>
                              </div>
                            );
                          })()
                        ) : sectionKey === 'honorsAndAwards' ? (
                          (() => {
                            const honors = getHonorsAndAwardsData();
                            if (!Array.isArray(honors) || honors.length === 0) {
                              return (
                                <p style={{ color: '#64748b', margin: 0 }}>
                                  No honors or awards detected on profile.
                                </p>
                              );
                            }

                            const SHOW_LIMIT = 5;
                            const displayedHonors = honors.slice(0, SHOW_LIMIT);
                            const hasMore = honors.length > SHOW_LIMIT;

                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {displayedHonors.map((honor, idx) => {
                                  const title = honor?.title || honor?.name || '';
                                  const issuer = honor?.issuer || honor?.organization || '';
                                  const dateRaw = honor?.date || honor?.issue_date || honor?.issued_on || '';
                                  const date = formatMonthYear(dateRaw);
                                  const description = honor?.description || '';

                                  if (!title && !issuer && !description) return null;

                                  return (
                                    <div
                                      key={`honor-${idx}`}
                                      style={{
                                        border: '1px solid rgba(226, 232, 240, 0.9)',
                                        borderRadius: 12,
                                        background: '#ffffff',
                                        boxShadow: '0 2px 10px rgba(15,23,42,0.04)',
                                        padding: '12px'
                                      }}
                                    >
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {title && (
                                          <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                            {title}
                                          </div>
                                        )}
                                        {(issuer || date) && (
                                          <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                            {[issuer, date].filter(Boolean).join(' · ')}
                                          </div>
                                        )}
                                        {description && (
                                          <div style={{ color: '#475569', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                                            <ReadMoreText text={description} limit={300} />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                {hasMore && (
                                  <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>
                                    Showing first {SHOW_LIMIT} honors. View your LinkedIn profile to see all.
                                  </p>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <p style={{ 
                            color: '#64748b', 
                            fontSize: '0.9rem', 
                            lineHeight: '1.5',
                            margin: 0
                          }}>
                            {data.current}
                          </p>
                        )}
                        </div>
                      </div>

                      {/* Optimized — hidden for profilePicture and banner since
                          those sections only show personalised suggestions. */}
                      {sectionKey !== 'profilePicture' && sectionKey !== 'banner' && (
                      <div style={{
                        padding: '1.5rem',
                        background: 'rgba(240, 253, 244, 0.8)',
                        borderRadius: '12px',
                        border: '1px solid rgba(16, 185, 129, 0.2)'
                      }}>
                        <h5 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#10b981',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          ✨ Optimization Strategy
                        </h5>
                        <div style={{ 
                          color: '#374151', 
                          fontSize: '0.9rem', 
                          lineHeight: '1.5',
                          margin: 0
                        }}>
                          {(() => {
                            // Check if profile picture or banner has score >= 8
                            const score = Number(data.score) || 0;
                            if ((sectionKey === 'profilePicture' || sectionKey === 'banner') && score >= 8) {
                              return (
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.75rem',
                                  padding: '1rem',
                                  background: 'rgba(16, 185, 129, 0.1)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(16, 185, 129, 0.2)'
                                }}>
                                  <span style={{ fontSize: '1.5rem' }}>🎉</span>
                                  <p style={{ margin: 0, color: '#047857', fontWeight: '500' }}>
                                    Your {sectionKey === 'profilePicture' ? 'profile picture' : 'banner image'} is already optimized. No changes needed.
                                  </p>
                                </div>
                              );
                            }

                            // Fixed content for Banner Image
                            if (sectionKey === 'banner') {
                              const bannerStrategies = [
                                'Treat your banner as a strategic billboard, not decoration - It should clearly show what you do, who you help, and how you create value. Optional: add subtle credibility signals like logos or client mentions.',
                                'Use clean, minimalist design built for clarity - Avoid clutter, stock photos, and random quotes. Use professional, high-resolution visuals with brand-aligned colors, fonts, and overall theme.',
                                'Write one bold, readable value statement - State clearly: Who you help and How you help. Use large, high-contrast, mobile-friendly text that is easy to read at a glance.',
                                'Apply correct layout and size for visibility - Use 1584×396 px (16:9) with a safe text zone of 1350×220 px. Place key text to the left-center so it is not covered by the profile photo.',
                                'Show trust without overloading - You may add subtle proof elements: brand logos, featured badges, or "Trusted by X+ clients" but keep it clean and uncluttered.'
                              ];
                              return (
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                  {bannerStrategies.map((strategy, idx) => (
                                    <li key={idx} style={{ 
                                      marginBottom: idx < bannerStrategies.length - 1 ? '0.8rem' : 0,
                                      position: 'relative',
                                      paddingLeft: '1.5rem'
                                    }}>
                                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                                      {strategy}
                                    </li>
                                  ))}
                                </ul>
                              );
                            }

                            // Fixed content for About Section
                            if (sectionKey === 'about') {
                              const aboutStrategies = [
                                'Use storytelling to communicate your journey, expertise, and why what you do matters.',
                                'Break it into clear sections: hook, credibility, achievements, and call to action.',
                                'Speak in first person to build human connection.',
                                'Add measurable impact to prove your credibility.',
                                'Include a clear call to connect, collaborate, or book a call.'
                              ];
                              return (
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                  {aboutStrategies.map((strategy, idx) => (
                                    <li key={idx} style={{ 
                                      marginBottom: idx < aboutStrategies.length - 1 ? '0.8rem' : 0,
                                      position: 'relative',
                                      paddingLeft: '1.5rem'
                                    }}>
                                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                                      {strategy}
                                    </li>
                                  ))}
                                </ul>
                              );
                            }

                            // Fixed content for Honors & Awards
                            if (sectionKey === 'honorsAndAwards') {
                              const honorsStrategies = [
                                'Highlight achievements that build credibility and authority.',
                                'Share only externally validated work: awards, patents, published works.',
                                'Include context: who awarded it, why it mattered.',
                                'Keep it updated and aligned with your current audience.',
                                'Attach links or proof wherever possible.'
                              ];
                              return (
                                <div>
                                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                    {honorsStrategies.map((strategy, idx) => (
                                      <li key={idx} style={{ 
                                        marginBottom: idx < honorsStrategies.length - 1 ? '0.8rem' : 0,
                                        position: 'relative',
                                        paddingLeft: '1.5rem'
                                      }}>
                                        <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                                        {strategy}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            }

                            // No splitting. The brain emits `optimized` as a JSON
                            // array of strings (one bullet per element). If we're
                            // handed an array, render each element directly. If it's
                            // a legacy string (old stored analysis), render as a
                            // single paragraph — no regex splitting.
                            if (Array.isArray(data.optimized)) {
                              const bullets = data.optimized.filter((s) => typeof s === 'string' && s.trim().length > 0);
                              if (bullets.length === 0) return <p style={{ margin: 0 }}></p>;
                              return (
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                  {bullets.map((bullet, idx) => (
                                    <li key={idx} style={{
                                      marginBottom: idx < bullets.length - 1 ? '0.8rem' : 0,
                                      position: 'relative',
                                      paddingLeft: '1.5rem'
                                    }}>
                                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                                      {bullet}
                                    </li>
                                  ))}
                                </ul>
                              );
                            }
                            return <p style={{ margin: 0 }}>{data.optimized}</p>;
                          })()}
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Personalised suggestions card with blur and unlock functionality */}
                    {/* Hide for profile picture/banner with score >= 8 */}
                    {(data.suggestions || data.optimized) && !((sectionKey === 'profilePicture' || sectionKey === 'banner') && (Number(data.score) || 0) >= 8) && (
                      <div 
                        style={{ 
                          position: 'relative',
                          padding: '1.5rem', 
                          background: 'linear-gradient(180deg, #F0F9FF 0%, #ECFEFF 100%)', 
                          borderRadius: '12px',
                          border: '1px solid rgba(14, 165, 233, 0.25)',
                          marginTop: '1.5rem',
                          cursor: isFullAccessUnlocked ? 'default' : 'pointer',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={isFullAccessUnlocked ? undefined : handleUnlockSuggestionsClick}
                        onMouseEnter={(e) => {
                          if (!isFullAccessUnlocked) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(14, 165, 233, 0.25)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isFullAccessUnlocked) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <h5 style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          color: '#0ea5e9', 
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          position: 'relative',
                          zIndex: isFullAccessUnlocked ? 1 : 3
                        }}>
                          🎯 Personalised Suggestions
                        </h5>
                        {/* Minimal Modern Blur overlay when locked */}
                        {!isFullAccessUnlocked && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(255, 255, 255, 0.75)',
                              backdropFilter: 'blur(12px)',
                              WebkitBackdropFilter: 'blur(12px)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              borderRadius: '12px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px',
                                maxWidth: '320px',
                                textAlign: 'center'
                              }}
                            >
                              {/* Minimal lock icon */}
                              <div
                                style={{
                                  width: '56px',
                                  height: '56px',
                                  borderRadius: '50%',
                                  background: 'rgba(14, 165, 233, 0.1)',
                                  border: '2px solid rgba(14, 165, 233, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <svg 
                                  width="24" 
                                  height="24" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="#0ea5e9" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                              </div>
                              
                              {/* Minimal text */}
                              <div>
                                <h4
                                  style={{
                                    margin: '0 0 8px',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#0f172a',
                                    letterSpacing: '-0.01em'
                                  }}
                                >
                                  Unlock to View
                                </h4>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: '0.875rem',
                                    color: '#64748b',
                                    lineHeight: '1.4'
                                  }}
                                >
                                  Click to access your personalized suggestions
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div style={{ 
                          color: '#374151', 
                          fontSize: '0.9rem', 
                          lineHeight: '1.6',
                          margin: 0,
                          filter: isFullAccessUnlocked ? 'none' : 'blur(4px)',
                          pointerEvents: isFullAccessUnlocked ? 'auto' : 'none',
                          transition: 'filter 0.5s ease'
                        }}>
                          {(() => {
                            // Fixed suggestions for Recommendations section
                            if (sectionKey === 'recommendations') {
                              const fixedRecommendationsSuggestions = [
                                'Request recommendations after you complete a project or deliver value.',
                                'Guide the writer to mention specific outcomes or impact.',
                                'Ask for recommendations that reflect technical, strategic, or leadership strengths.',
                                'Offer to write recommendations for others. This often prompts returns.',
                                'Display a mix of roles recommending you. It adds credibility.'
                              ];
                              return (
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                  {fixedRecommendationsSuggestions.map((suggestion, idx) => (
                                    <li key={idx} style={{ 
                                      marginBottom: idx < fixedRecommendationsSuggestions.length - 1 ? '1.2rem' : 0,
                                      position: 'relative',
                                      paddingLeft: '1.5rem',
                                      background: '#fff',
                                      padding: '0.7rem 1rem 0.7rem 2rem',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(226, 232, 240, 1)',
                                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                    }}>
                                      <span style={{ position: 'absolute', left: '0.7rem', color: '#0ea5e9', fontWeight: '600' }}>•</span>
                                      {suggestion}
                                    </li>
                                  ))}
                                </ul>
                              );
                            }

                            // Fixed suggestions for Honors & Awards section
                            if (sectionKey === 'honorsAndAwards') {
                              const fixedHonorsSuggestions = [
                                'List only achievements that strengthen your credibility.',
                                'Briefly describe what it was, when it happened, and why it matters.',
                                'Include proof when possible like publications, articles, or certificates.',
                                'Position entries as outcomes of your effort and expertise.',
                                'Keep the format consistent and clear.'
                              ];
                              return (
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                                  {fixedHonorsSuggestions.map((suggestion, idx) => (
                                    <li key={idx} style={{ 
                                      marginBottom: idx < fixedHonorsSuggestions.length - 1 ? '1.2rem' : 0,
                                      position: 'relative',
                                      paddingLeft: '1.5rem',
                                      background: '#fff',
                                      padding: '0.7rem 1rem 0.7rem 2rem',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(226, 232, 240, 1)',
                                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                    }}>
                                      <span style={{ position: 'absolute', left: '0.7rem', color: '#0ea5e9', fontWeight: '600' }}>•</span>
                                      {suggestion}
                                    </li>
                                  ))}
                                </ul>
                              );
                            }

                            // Use suggestions if available, otherwise fallback to optimized content
                            const contentToUse = data.suggestions || data.optimized;
                            const isActualSuggestions = Boolean(data.suggestions);
                            
                            // Show notice if using fallback content
                            if (!isActualSuggestions) {
                              return (
                                <div style={{ 
                                  padding: '1rem',
                                  background: '#FFF3CD',
                                  border: '1px solid #FFEAA7',
                                  borderRadius: '8px',
                                  color: '#856404'
                                }}>
                                  <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                    🔄 Personalized suggestions will appear here after your next AI analysis. 
                                    Currently showing optimization strategy as placeholder.
                                  </p>
                                </div>
                              );
                            }
                            
                            // About section's sugAbout is a single flowing paragraph
                            // by design. Render as-is. If the model ever returns
                            // literal \n\n breaks, whitespace: pre-line preserves
                            // them without any regex splitting on our side.
                            if (sectionKey === 'about' && typeof contentToUse === 'string') {
                              return (
                                <div style={{
                                  background: '#fff',
                                  padding: '1.5rem',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(226, 232, 240, 0.8)',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                  <p style={{
                                    margin: 0,
                                    color: '#374151',
                                    fontSize: '0.95rem',
                                    lineHeight: '1.75',
                                    textAlign: 'left',
                                    whiteSpace: 'pre-line'
                                  }}>
                                    {contentToUse}
                                  </p>
                                </div>
                              );
                            }
                            
                            // Render exactly what the AI returned. Brain output for
                            // suggestions is now a JSON array of strings (one bullet
                            // per element) — we iterate it directly. Legacy string
                            // data (old stored analyses) renders as a single block
                            // with newlines preserved; no regex splitting.
                            let bullets = [];
                            if (Array.isArray(contentToUse)) {
                              bullets = contentToUse.filter(s => typeof s === 'string' && s.trim().length > 0);
                            } else if (typeof contentToUse === 'string' && contentToUse.trim()) {
                              // Legacy fallback: render the whole string as one bullet
                              // rather than splitting it. Nothing on our side parses.
                              bullets = [contentToUse.trim()];
                            }
                            
                            if (bullets.length > 0) {
                              return (
                                <ul style={{ 
                                  margin: 0, 
                                  paddingLeft: '1.2rem',
                                  listStyleType: 'none'
                                }}>
                                  {bullets.map((bullet, idx) => {
                                    return (
                                      <li key={idx} style={{ 
                                        marginBottom: idx < bullets.length - 1 ? '1.2rem' : 0,
                                        position: 'relative',
                                        paddingLeft: '1.5rem',
                                        background: '#fff',
                                        padding: '0.7rem 1rem 0.7rem 2rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(226, 232, 240, 1)',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                      }}>
                                        <span style={{
                                          position: 'absolute',
                                          left: '0.7rem',
                                          color: '#0ea5e9',
                                          fontWeight: '600'
                                        }}>•</span>
                                        {bullet}
                                      </li>
                                    );
                                  })}
                                </ul>
                              );
                            } else {
                              // Fallback: display as plain text if no bullets detected
                              return (
                                <div style={{ 
                                  margin: 0, 
                                  whiteSpace: 'pre-wrap',
                                  background: '#fff',
                                  padding: '0.8rem 1rem',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(226, 232, 240, 1)'
                                }}>
                                  {contentToUse}
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              });
          })()}

          {/* NEW SECTIONS: Skills, Languages, Volunteering, Featured */}
          
          {/* Skills & Endorsements Section */}
          {(() => {
            const skills = getSkillsData();
            if (!skills.length) return null;
            const displaySkills = skills.slice(0, 20); // Show top 20 skills
            
            const skillsStrategies = [
              'Carefully select skills that match your positioning and goals.',
              'Your top 3 skills should reflect your core expertise.',
              'Ensure these skills are endorsed by credible professionals.',
              'Limit to relevant skills. Avoid cluttering with unrelated abilities.',
              'Include a mix of hard, technical, and leadership skills.'
            ];
            
            const skillsSuggestions = [
              'Reorder your skills so the top 3 reflect what you want to be known for.',
              'Add niche-specific skills that improve searchability and positioning.',
              'Ask colleagues or clients to endorse skills that matter most to your goals.',
              'Remove outdated or non-core skills to stay focused.',
              'Regularly update skills to reflect your growth and learning.'
            ];
            
            return (
              <div id="section-skills" className="card" style={{ marginTop: '2rem', scrollMarginTop: '90px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>💡</span>
                  Skills & Endorsements
                  <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#64748b', marginLeft: 'auto' }}>
                    {skills.length} {skills.length === 1 ? 'skill' : 'skills'}
                  </span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '1.5rem' }}>
                  {displaySkills.map((skill, idx) => (
                    <div key={`skill-${idx}`} style={{ 
                      padding: '16px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '12px', 
                      background: '#fff',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                      transition: 'all 0.2s ease',
                      cursor: 'default'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,23,42,0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.04)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.95rem' }}>{skill.name || 'Skill'}</div>
                        {skill.endorsement_count > 0 && (
                          <div style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: '#10b981', 
                            background: 'rgba(16, 185, 129, 0.1)', 
                            padding: '4px 10px', 
                            borderRadius: '12px' 
                          }}>
                            {skill.endorsement_count} endorsements
                          </div>
                        )}
                      </div>
                      {skill.related_experiences && skill.related_experiences.length > 0 && (
                        <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '6px' }}>
                          {skill.related_experiences.slice(0, 2).join(' • ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Optimization Strategy for Skills */}
                <div style={{ 
                  padding: '1.5rem', 
                  background: 'rgba(240, 253, 244, 0.8)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  marginBottom: '1.5rem'
                }}>
                  <h5 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#10b981', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ✨ Optimization Strategy
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none', color: '#374151', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    {skillsStrategies.map((strategy, idx) => (
                      <li key={idx} style={{ 
                        marginBottom: idx < skillsStrategies.length - 1 ? '0.8rem' : 0,
                        position: 'relative',
                        paddingLeft: '1.5rem'
                      }}>
                        <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                        {strategy}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Fixed Personalized Suggestions for Skills */}
                <div style={{ 
                  padding: '1.5rem', 
                  background: 'linear-gradient(180deg, #F0F9FF 0%, #ECFEFF 100%)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(14, 165, 233, 0.25)'
                }}>
                  <h5 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#0ea5e9', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🎯 Personalised Suggestions
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                    {skillsSuggestions.map((suggestion, idx) => (
                      <li key={idx} style={{ 
                        marginBottom: idx < skillsSuggestions.length - 1 ? '1.2rem' : 0,
                        position: 'relative',
                        paddingLeft: '1.5rem',
                        background: '#fff',
                        padding: '0.7rem 1rem 0.7rem 2rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(226, 232, 240, 1)',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        color: '#374151',
                        fontSize: '0.9rem'
                      }}>
                        <span style={{ position: 'absolute', left: '0.7rem', color: '#0ea5e9', fontWeight: '600' }}>•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}

          {/* Languages Section - REMOVED as per requirements */}

          {/* Featured Content Section */}
          {(() => {
            const featured = getFeaturedData();
            if (!featured.length) return null;
            
            const featuredStrategies = [
              'Showcase content that proves your authority and credibility.',
              'Feature posts, presentations, articles, or interviews with strong engagement.',
              'Place your most trust-building asset first: keynote, major achievement, or flagship post.',
              'Update often to keep it relevant and active.',
              'Use actionable titles to attract clicks and conversions.'
            ];
            
            const featuredSuggestions = [
              'Include a mix of high-performing posts, articles, case studies, or media features.',
              'Feature your most credible or recent achievements at the top.',
              'Write short captions that show results or what viewers will gain.',
              'Consider adding links to your portfolio, booking page, or thought leadership content.',
              'Refresh this section anytime you launch something, speak at events, or get featured.'
            ];
            
            return (
              <div id="section-featured" className="card" style={{ marginTop: '2rem', scrollMarginTop: '90px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>⭐</span>
                  Featured Content
                  <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#64748b', marginLeft: 'auto' }}>
                    {featured.length} {featured.length === 1 ? 'item' : 'items'}
                  </span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', marginBottom: '1.5rem' }}>
                  {featured.map((item, idx) => (
                    <a
                      key={`featured-${idx}`}
                      href={item.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                        border: '1px solid #e2e8f0', 
                        borderRadius: '12px', 
                        background: '#fff',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(15,23,42,0.12)';
                        e.currentTarget.style.transform = 'translateY(-4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.04)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {item.image_url && (
                        <div style={{
                          width: '100%',
                          height: '180px',
                          background: `url(${proxyImage(item.image_url)}) center/cover no-repeat`,
                          backgroundColor: '#f1f5f9'
                        }} />
                      )}
                      <div style={{ padding: '16px' }}>
                        {item.type && (
                          <div style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: '#6366f1', 
                            background: 'rgba(99, 102, 241, 0.1)', 
                            padding: '4px 8px', 
                            borderRadius: '6px',
                            display: 'inline-block',
                            marginBottom: '10px',
                            textTransform: 'capitalize'
                          }}>
                            {item.type}
                          </div>
                        )}
                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '1rem', marginBottom: '6px' }}>
                          {item.title || 'Featured Item'}
                        </div>
                        {item.description && (
                          <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                            {item.description.length > 120 
                              ? item.description.substring(0, 120) + '...' 
                              : item.description}
                          </div>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
                
                {/* Optimization Strategy for Featured */}
                <div style={{ 
                  padding: '1.5rem', 
                  background: 'rgba(240, 253, 244, 0.8)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  marginBottom: '1.5rem'
                }}>
                  <h5 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#10b981', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ✨ Optimization Strategy
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none', color: '#374151', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    {featuredStrategies.map((strategy, idx) => (
                      <li key={idx} style={{ 
                        marginBottom: idx < featuredStrategies.length - 1 ? '0.8rem' : 0,
                        position: 'relative',
                        paddingLeft: '1.5rem'
                      }}>
                        <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: '600' }}>•</span>
                        {strategy}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Fixed Personalized Suggestions for Featured */}
                <div style={{ 
                  padding: '1.5rem', 
                  background: 'linear-gradient(180deg, #F0F9FF 0%, #ECFEFF 100%)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(14, 165, 233, 0.25)'
                }}>
                  <h5 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#0ea5e9', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🎯 Personalised Suggestions
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyleType: 'none' }}>
                    {featuredSuggestions.map((suggestion, idx) => (
                      <li key={idx} style={{ 
                        marginBottom: idx < featuredSuggestions.length - 1 ? '1.2rem' : 0,
                        position: 'relative',
                        paddingLeft: '1.5rem',
                        background: '#fff',
                        padding: '0.7rem 1rem 0.7rem 2rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(226, 232, 240, 1)',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        color: '#374151',
                        fontSize: '0.9rem'
                      }}>
                        <span style={{ position: 'absolute', left: '0.7rem', color: '#0ea5e9', fontWeight: '600' }}>•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}

          {/* Volunteering Experience Section - Last Section */}
          {(() => {
            const volunteering = getVolunteeringData();
            if (!volunteering.length) return null;
            
            return (
              <div className="card" style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🤝</span>
                  Volunteering Experience
                  <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#64748b', marginLeft: 'auto' }}>
                    {volunteering.length} {volunteering.length === 1 ? 'role' : 'roles'}
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {volunteering.map((vol, idx) => (
                    <div key={`vol-${idx}`} style={{ 
                      padding: '20px', 
                      border: '1px solid rgba(251, 191, 36, 0.3)', 
                      borderRadius: '12px', 
                      background: 'linear-gradient(135deg, rgba(254, 243, 199, 0.3) 0%, rgba(253, 230, 138, 0.2) 100%)',
                      boxShadow: '0 2px 8px rgba(251, 191, 36, 0.1)'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr', gap: '14px', alignItems: 'start' }}>
                        <div style={{ 
                          width: '48px', 
                          height: '48px', 
                          borderRadius: '12px', 
                          background: 'rgba(251, 191, 36, 0.2)', 
                          color: '#f59e0b', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: '24px',
                          fontWeight: '600'
                        }}>
                          🎯
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '1.05rem', marginBottom: '4px' }}>
                            {vol.title || 'Volunteer Position'}
                          </div>
                          <div style={{ color: '#78350f', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px' }}>
                            {vol.organization || vol.organization_name || 'Organization'}
                          </div>
                          {vol.duration && (
                            <div style={{ color: '#92400e', fontSize: '0.9rem', marginBottom: '8px' }}>
                              {vol.duration}
                            </div>
                          )}
                          {vol.sector && (
                            <div style={{ 
                              display: 'inline-block',
                              fontSize: '0.8rem', 
                              fontWeight: '600', 
                              color: '#f59e0b', 
                              background: 'rgba(251, 191, 36, 0.2)', 
                              padding: '4px 10px', 
                              borderRadius: '8px',
                              marginBottom: '8px'
                            }}>
                              {vol.sector}
                            </div>
                          )}
                          {vol.description && (
                            <div style={{ color: '#78350f', fontSize: '0.92rem', marginTop: '10px', lineHeight: '1.5' }}>
                              {vol.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>
      </div>
    </div>
    );
  };

  
  
  // (Action Plan removed)

  const RoadmapTab = () => (
    <div>
      <PriorityFilter />
      <div style={{ marginBottom: '2rem' }}>
        <h3 className="section-title">4-Week Implementation Roadmap</h3>
        <p className="section-subtitle">
          Strategic timeline to transform your LinkedIn presence
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {results.weeklyPlan.map((week, index) => (
          <div key={index} className="card" style={{ 
            border: '2px solid #e2e8f0',
            background: index === 0 ? '#f0f9ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                background: index === 0 ? '#0077B5' : '#94a3b8',
                color: 'white',
                borderRadius: '12px',
                fontSize: '1.25rem',
                fontWeight: '700'
              }}>
                W{week.week}
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Week {week.week}: {week.focus}
                </h4>
                {index === 0 && (
                  <span style={{ 
                    fontSize: '0.875rem', 
                    color: '#0077B5',
                    fontWeight: '500'
                  }}>
                    🚀 Start here
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-2" style={{ gap: '1rem' }}>
              <div>
                <h5 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                  📋 Week {week.week} Tasks:
                </h5>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {week.tasks.map((task, taskIndex) => (
                <div key={taskIndex} style={{
                  padding: '1rem 1.25rem',
                  background: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  <CheckCircle className="w-4 h-4" style={{ color: '#10b981', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem', color: '#374151', lineHeight: '1.4' }}>{task}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{
                padding: '1rem',
                background: week.expectedGain ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                borderRadius: '8px',
                border: `1px solid ${week.expectedGain ? 'rgba(16, 185, 129, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: week.expectedGain ? '#059669' : '#6366f1', marginBottom: '0.25rem' }}>
                  📈 Expected Gain
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: week.expectedGain ? '#10b981' : '#6366f1' }}>
                  {week.expectedGain || '+2-5 points'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Calendar */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar className="w-5 h-5" style={{ color: '#0077B5' }} />
          Content Planning Template
        </h3>
        
        <div className="grid grid-2" style={{ marginTop: '1rem' }}>
          <div>
            <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#374151' }}>Weekly Content Mix</h4>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {[
                'Monday: Industry insights & trends',
                'Wednesday: Leadership perspectives',
                'Friday: Team/company achievements'
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '0.5rem',
                  background: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#374151' }}>Post Ideas</h4>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {[
                'Share a recent business win',
                'Comment on industry developments',
                'Highlight team member success'
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '0.5rem',
                  background: '#f0f9ff',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  border: '1px solid #bfdbfe'
                }}>
                  <Lightbulb className="w-3 h-3" style={{ display: 'inline', marginRight: '0.5rem', color: '#3b82f6' }} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="section dashboard-fullscreen" style={{ paddingTop: '16px' }}>
      {/* Payment Processing Loading Overlay */}
      {isProcessingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: '0 0 0.5rem' }}>
              Loading Payment Gateway...
            </p>
            <p style={{ fontSize: '0.95rem', color: '#64748b', margin: 0 }}>
              Please wait while Razorpay checkout loads
            </p>
          </div>
        </div>
      )}

      {/* Modern Light Floating Navigation */}
      <nav className="dashboard-nav" style={{
        position: 'sticky',
        top: '4px',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '50px',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        padding: '6px 8px',
        margin: '0 auto 1rem',
        width: 'fit-content',
        maxWidth: 'calc(100vw - 24px)'
      }}>
        {/* Progress bar at bottom */}
        <div style={{
          position: 'absolute',
          bottom: '3px',
          left: '12px',
          right: '12px',
          height: '2px',
          background: 'rgba(0, 0, 0, 0.06)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
            borderRadius: '2px',
            transition: 'width 0.15s ease-out'
          }} />
        </div>
        
        {/* Desktop Navigation */}
        <div className="nav-desktop" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          padding: '2px 4px 6px'
        }}>
          {navSections.filter(s => {
            if (s.key === 'skills' || s.key === 'featured') return true;
            return results?.sections?.[s.key];
          }).map((section) => (
            <button
              key={section.key}
              onClick={() => scrollToSection(section.key)}
              style={{
                padding: '8px 14px',
                borderRadius: '20px',
                border: 'none',
                background: activeSection === section.key 
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                  : 'transparent',
                color: activeSection === section.key ? 'white' : '#475569',
                fontSize: '13px',
                fontWeight: activeSection === section.key ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                boxShadow: activeSection === section.key 
                  ? '0 2px 8px rgba(99, 102, 241, 0.3)' 
                  : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== section.key) {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)';
                  e.currentTarget.style.color = '#6366f1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.key) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#475569';
                }
              }}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Mobile Navigation Toggle */}
        <div className="nav-mobile" style={{ display: 'none' }}>
          <button
            onClick={() => setShowMobileNav(!showMobileNav)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '20px',
              color: '#1e293b',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
              }} />
              Sections
            </span>
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5"
              style={{ 
                transform: showMobileNav ? 'rotate(180deg)' : 'rotate(0)', 
                transition: 'transform 0.2s ease'
              }}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          {/* Mobile Dropdown */}
          {showMobileNav && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '16px',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              padding: '10px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '4px'
            }}>
              {navSections.filter(s => {
                if (s.key === 'skills' || s.key === 'featured') return true;
                return results?.sections?.[s.key];
              }).map((section) => (
                <button
                  key={section.key}
                  onClick={() => scrollToSection(section.key)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: activeSection === section.key 
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                      : 'rgba(248, 250, 252, 0.8)',
                    color: activeSection === section.key ? 'white' : '#334155',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {section.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      <div className="container">
        {/* Auto-Download Notification */}
        {isAutoDownloading && (
          <div style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
            color: 'white',
            padding: '16px 32px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(6, 182, 212, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <Download style={{ width: 20, height: 20, animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontWeight: 600, fontSize: '15px' }}>
              {isGeneratingPDF ? 'Generating your PDF report...' : 'Preparing PDF download...'}
            </span>
          </div>
        )}
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          {/* Back to Hero - Inline Confirmation. Hidden in sample-audit mode. */}
          {!isSample && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.75rem' }}>
            {!showExitModal ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  padding: '8px 16px',
                  borderRadius: '999px',
                  background: 'white',
                  border: '1px solid #dbeafe',
                  color: '#1d4ed8',
                  boxShadow: '0 4px 10px rgba(15, 23, 42, 0.06)',
                  transition: 'all 0.2s ease'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
            ) : (
              <div 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)',
                  borderRadius: '999px',
                  border: '1px solid #bfdbfe',
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <span style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500, 
                  color: '#1e3a8a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <ArrowLeft size={16} />
                  Leave dashboard?
                </span>
                <button
                  type="button"
                  onClick={handleConfirmBack}
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'white',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.35)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.45)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.35)';
                  }}
                >
                  Yes, leave
                </button>
                <button
                  type="button"
                  onClick={handleCancelBack}
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  Stay
                </button>
              </div>
            )}
          </div>
          )}
          <h1 className="hero-title" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
            Your LinkedIn Profile report
          </h1>
          <div style={{ 
            padding: '12px 20px', 
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', 
            borderRadius: '12px',
            border: '2px solid #3b82f6',
            marginBottom: '1.5rem',
            display: 'inline-block'
          }}>
            <p style={{ 
              fontSize: '0.9rem', 
              color: '#1e40af', 
              margin: 0,
              fontWeight: 600
            }}>
              📊 Analyzing Profile: <a href={profileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>{profileUrl}</a>
            </p>
          </div>
          
          {/* Action Buttons removed as requested */}
        </div>

        {/* Tab Navigation moved into each tab section */}

        {/* Tab Content */}
        <div>
          {activeTab === 'feedback' && <FeedbackTab />}
        </div>

        {/** Footer CTA removed as requested **/}

        {/* Payment modal removed - going directly to Razorpay */}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes slideIn {
          0% { 
            opacity: 0;
            transform: translateX(-10px);
          }
          100% { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideDown {
          0% { 
            transform: translate(-50%, -100%); 
            opacity: 0;
          }
          100% { 
            transform: translate(-50%, 0); 
            opacity: 1;
          }
        }
        
        /* Navigation Bar Responsive */
        .nav-desktop {
          display: flex !important;
        }
        .nav-mobile {
          display: none !important;
        }
        .nav-desktop::-webkit-scrollbar {
          display: none;
        }
        
        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          /* Navigation bar mobile */
          .nav-desktop {
            display: none !important;
          }
          .nav-mobile {
            display: block !important;
          }
          
          /* Dashboard container */
          .dashboard-fullscreen {
            padding: 1rem 0.5rem !important;
          }
          
          /* Download Report Button - Mobile */
          .dashboard-fullscreen button[disabled] {
            padding: 12px 20px !important;
            font-size: 0.9rem !important;
            gap: 8px !important;
          }
          
          .dashboard-fullscreen button[disabled] span:first-of-type {
            width: 28px !important;
            height: 28px !important;
          }
          
          /* Priority Filter - Stack on mobile */
          .dashboard-fullscreen > div > div:first-child {
            flex-direction: column !important;
            gap: 12px !important;
            align-items: stretch !important;
          }
          
          .dashboard-fullscreen > div > div:first-child > button:first-child {
            width: 100% !important;
            justify-content: center !important;
          }
          
          /* Priority Filter buttons - More compact on mobile */
          .dashboard-fullscreen > div > div > div > div > button {
            padding: 6px 10px !important;
            font-size: 0.85rem !important;
            gap: 6px !important;
            -webkit-tap-highlight-color: transparent;
          }
          
          .dashboard-fullscreen > div > div > div > div > button span {
            width: 6px !important;
            height: 6px !important;
          }
          
          /* Priority filter container */
          .dashboard-fullscreen > div > div > div > div {
            padding: 4px !important;
            justify-content: center !important;
            width: 100% !important;
          }
          
          /* Score Hero - Smaller on mobile */
          .card h2 {
            font-size: 2rem !important;
          }
          
          .card .progress-container {
            max-width: 100% !important;
          }
          
          /* Feedback cards */
          .card {
            padding: 1.25rem !important;
            margin-bottom: 1rem !important;
          }
          
          .card h4 {
            font-size: 1rem !important;
          }
          
          .card p {
            font-size: 0.9rem !important;
          }
          
          /* Copy buttons */
          .card button {
            padding: 6px 10px !important;
            font-size: 0.8rem !important;
          }
          
          /* Download Modal - Mobile */
          .download-modal {
            width: 95% !important;
            padding: 24px 20px !important;
            max-height: 85vh !important;
            border-radius: 16px !important;
          }
          
          .download-modal h3 {
            font-size: 1.2rem !important;
          }
          
          .download-modal input,
          .download-modal textarea {
            font-size: 16px !important; /* Prevents zoom on iOS */
            padding: 12px !important;
          }
          
          .download-modal button[disabled] {
            padding: 14px 12px !important;
            font-size: 0.95rem !important;
            -webkit-tap-highlight-color: transparent;
          }
          
          .download-modal label {
            font-size: 0.9rem !important;
          }
        }
        
        @media (max-width: 480px) {
          /* Extra small screens */
          .dashboard-fullscreen {
            padding: 0.75rem 0.25rem !important;
          }
          
          .card {
            padding: 1rem !important;
          }
          
          .card h2 {
            font-size: 1.75rem !important;
          }
          
          .dashboard-fullscreen button[disabled] {
            padding: 10px 16px !important;
            font-size: 0.85rem !important;
          }
          
          /* Download Modal - Extra Small Screens */
          .download-modal {
            width: 96% !important;
            padding: 20px 16px !important;
            max-height: 90vh !important;
          }
          
          .download-modal h3 {
            font-size: 1.1rem !important;
          }
          
          .download-modal p {
            font-size: 0.85rem !important;
          }
          
          .download-modal > div:first-child > div:first-child {
            width: 48px !important;
            height: 48px !important;
            margin-bottom: 10px !important;
          }
          
          .download-modal > div:first-child > div:first-child svg {
            width: 22px !important;
            height: 22px !important;
          }
          
          .download-modal input {
            padding: 10px !important;
          }
          
          .download-modal button {
            padding: 12px !important;
            font-size: 0.9rem !important;
          }
        }
        
        /* Prevent zoom on input focus for iOS */
        @media (max-width: 768px) {
          input[type="text"],
          input[type="email"],
          input[type="tel"],
          textarea {
            font-size: 16px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Dashboard;
